import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { getCurrentUserId } from 'common-src/features/auth';
import { deleteOrmItem } from 'common-src/features/rest';
import { changeClientPlatformRequest, Platform } from 'common-src/models/Client';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import {
  normalizeAppointment,
  syncDashboardV2Appointments,
} from 'common-src/models/DashboardV2Appointment/sync';
import { getDashboardV2Tasks } from 'common-src/models/DashboardV2Task';
import {
  normalizeDashboardV2Task,
  upsertDashboardV2Task,
} from 'common-src/models/DashboardV2Task/sync';
import { getFeatureFlags, isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { PlatformStatus } from 'common-src/models/Patient';
import { isTestAthenaDepartment } from 'common-src/models/PatientIntegrationsAthena';
import { PatientTaskStatus } from 'common-src/models/PatientTask';
import { updatePatientTask } from 'common-src/models/PatientTask/actions';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import {
  getCareOrchestrationCapacityTasks,
  getCareOrchestrationIsLoading,
  getCareOrchestrationMeta,
  setCareOrchestrationCapacityTasks,
  setCareOrchestrationError,
  setCareOrchestrationLastFetchedAt,
  setCareOrchestrationLoading,
  setCareOrchestrationMeta,
  setCareOrchestrationStats,
} from 'src/features/careOrchestration';
import {
  getCareOrchestrationState,
  saveCareOrchestrationState,
  saveCareOrchestrationView,
} from 'src/features/tabsState';
import useAlert from 'src/hooks/useAlert';
import useAthenaDepartments from 'src/hooks/useAthenaDepartments';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useDocumentTitle from 'src/hooks/useDocumentTitle';
import useInterval from 'src/hooks/useInterval';

import styles from './CareOrchestration.module.scss';
import BulkAssignToast from './components/BulkAssignToast';
import BulkAssignView from './components/BulkAssignView';
import CareOrchestrationHeader from './components/CareOrchestrationHeader';
import LeadTasksView from './components/LeadTasksView';
import ReadinessView from './components/ReadinessView';
import TaskNotificationToasts from './components/TaskNotificationToasts';
import TasksView from './components/TasksView';
import {
  CARE_ORCHESTRATION_BASE_PATH,
  DEFAULT_NOTIFICATION_PREFERENCES,
  DEFAULT_OWNER_STATS,
  DEFAULT_STATS,
  MAX_TASK_NOTIFICATION_TOASTS,
  NOTIFICATION_CATEGORY,
  NOTIFICATION_CATEGORY_KEY_BY_ID,
  NOTIFICATION_STATUS,
  OrgScope,
  PRIORITY_SORT_ORDER,
  TAB_KEYS,
  TASK_DISPLAY_STATUS,
  TASK_NOTIFICATION_TOAST_STAGGER_MS,
  TASK_NOTIFICATION_TOASTED_IDS_MAX,
  TASK_NOTIFICATION_TOASTED_IDS_STORAGE_KEY,
  TASK_PRIORITY,
  TASK_QUEUE_PAGE_SIZE,
  TASK_QUEUE_SORT_MODE,
  WORKFLOW_SCOPE,
} from './constants';
import { CORTEX_ORIGIN } from './cortexLinks';
import {
  getAppointmentReadinessStatus,
  getCareOrchestrationPath,
  getTaskBrowserNotificationPermission,
  getTaskDisplayStatus,
  getTaskPatientGroupKey,
  getTaskPatientId,
  groupAppointmentsByDate,
  isAssessmentTask,
  isBrowserNotificationsSupported,
  isEscalationPayloadValid,
  isMessageTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
  isTaskNotificationChannelEnabled,
  matchesTaskStatusFilter,
  normalizeEscalationPayload,
  parseCareOrchestrationRoute,
  playTaskNotificationSound,
  requestTaskBrowserNotificationPermission,
  shouldHideTaskByCallbackStatus,
  showTaskBrowserNotification,
  sortAppointmentsByDate,
  unlockTaskNotificationSound,
} from './helpers';
import {
  departmentsInScope,
  EMPTY_SCOPE_FILTERS,
  filtersEqualToPinnedScope,
  hasActiveScopeFilters,
  scopeToPinnedPayload,
} from './scope';
import {
  assignTaskToMe,
  blockTask,
  bulkReassignTasks,
  cancelTask,
  deescalateTask,
  delegateSubtasks,
  escalateTask,
  fetchDashboardStatsData,
  fetchDashboardSupplementalData,
  fetchReadinessAppointmentsData,
  fetchTaskQueueChanges,
  fetchTaskQueueData,
  fetchTaskQueueItemById,
  fetchTaskQueueMetaData,
  fetchTaskSearchPatients,
  fetchTaskSubtasks,
  fetchTaskSubtasksBulk,
  followTask,
  getMyPinnedDepartmentScope,
  getMyReadinessPinnedTeam,
  getMyTaskNotificationPreferences,
  getMyTaskNotifications,
  getMyTaskNotificationsStreamUrl,
  markAllMyTaskNotificationsRead,
  markTaskComplete,
  patchMyPinnedDepartmentScope,
  patchMyReadinessPinnedTeam,
  patchMyTaskNotificationPreferences,
  patchMyTaskNotificationStatus,
  reassignTask,
  searchMyTaskNotificationSlackUsers,
  sendMyTaskNotificationTestMessage,
  unassignTask,
  unblockTask,
  unfollowTask,
} from './services/careOrchestrationApi';
import {
  syncCareOrchestrationData,
  syncEmbeddedIntegrations,
} from './services/syncCareOrchestrationData';

const isTaskFollowStateAlreadyCurrentError = (errorMessage = '') => {
  const normalizedMessage = `${errorMessage}`.trim().toLowerCase();

  return (
    normalizedMessage.includes('task is already followed') ||
    normalizedMessage.includes('task is already unfollowed') ||
    normalizedMessage.includes('task is not being followed')
  );
};

const hasTaskQueueMetaChanged = (previousMeta, nextMeta) =>
  JSON.stringify(previousMeta || null) !== JSON.stringify(nextMeta || null);

const mergeTaskQueueMeta = (previousMeta, nextMeta) => {
  if (!previousMeta) return nextMeta;
  if (!nextMeta) return previousMeta;

  return Object.entries(nextMeta).reduce(
    (acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    },
    { ...previousMeta },
  );
};

const hasFullTaskQueueMeta = (meta) =>
  meta?.totalUncompleted !== null && meta?.totalUncompleted !== undefined;

const TASK_QUEUE_CHANGES_POLL_INTERVAL_MS = 30000;
const READINESS_APPOINTMENT_WINDOWS = [
  { key: 'next14Days', label: 'next 2 weeks', startOffsetDays: 0, endOffsetDays: 14 },
  { key: 'next90Days', label: 'next 90 days', startOffsetDays: 15, endOffsetDays: 90 },
];
const INITIAL_READINESS_APPOINTMENT_WINDOW = READINESS_APPOINTMENT_WINDOWS[0];

const getAppointmentWorkflowKey = (appointmentId, workflowInstanceId = null) =>
  `${appointmentId}::${workflowInstanceId || 'appointment'}`;

const getPlatformScope = (defaultPlatform) =>
  defaultPlatform === Platform.Mindspan ? OrgScope.Mindspan : OrgScope.Perry;

const formatDateOnly = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getReadinessWindowDateRange = (readinessWindow) => {
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  startDate.setDate(startDate.getDate() + readinessWindow.startOffsetDays);

  const endDate = new Date();
  endDate.setHours(0, 0, 0, 0);
  endDate.setDate(endDate.getDate() + readinessWindow.endOffsetDays);

  return {
    dateFrom: formatDateOnly(startDate),
    dateTo: formatDateOnly(endDate),
  };
};

const sortReadinessTasks = (tasks = []) =>
  [...tasks].sort((a, b) => {
    const aPriority = a?.priority ?? '';
    const bPriority = b?.priority ?? '';
    const priorityDiff =
      (PRIORITY_SORT_ORDER[aPriority] ?? Number.POSITIVE_INFINITY) -
      (PRIORITY_SORT_ORDER[bPriority] ?? Number.POSITIVE_INFINITY);
    if (priorityDiff !== 0) return priorityDiff;

    const aDue = a?.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
    const bDue = b?.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
    return aDue - bDue;
  });

const normalizeReadinessAppointmentTasksById = (responseBody) => {
  const rawAppointmentTasksById =
    responseBody?.appointmentTasksById ?? responseBody?.appointment_tasks_by_id ?? {};

  const normalizedTasksById = Object.entries(rawAppointmentTasksById).reduce(
    (acc, [lookupKey, appointmentTasks]) => ({
      ...acc,
      [lookupKey]: sortReadinessTasks(
        (appointmentTasks || []).map(normalizeDashboardV2Task).filter((task) => !task?.isSubtask),
      ),
    }),
    {},
  );

  (responseBody?.data || []).forEach((appointment) => {
    const appointmentId = `${appointment?.id || ''}`.trim();
    const workflowInstanceId = `${
      appointment?.workflowInstanceId ?? appointment?.workflow_instance_id ?? ''
    }`.trim();
    const appointmentTasks = appointment?.tasks || [];
    if (!appointmentId || !Array.isArray(appointmentTasks)) return;

    const normalizedAppointmentTasks = sortReadinessTasks(
      appointmentTasks.map(normalizeDashboardV2Task).filter((task) => !task?.isSubtask),
    );
    normalizedTasksById[appointmentId] = normalizedAppointmentTasks;

    if (workflowInstanceId) {
      normalizedTasksById[getAppointmentWorkflowKey(appointmentId, workflowInstanceId)] =
        normalizedAppointmentTasks;
    }
  });

  return normalizedTasksById;
};

const normalizeReadinessAppointmentSubtasksByParentId = (responseBody) =>
  (responseBody?.data || []).reduce((acc, appointment) => {
    const appointmentTasks = appointment?.tasks || [];
    if (!Array.isArray(appointmentTasks)) return acc;

    appointmentTasks.map(normalizeDashboardV2Task).forEach((task) => {
      if (!task?.isSubtask) return;
      const parentTaskId = `${task?.parentTaskId || ''}`.trim();
      if (!parentTaskId) return;

      acc[parentTaskId] = acc[parentTaskId] || [];
      acc[parentTaskId].push(task);
    });

    return Object.entries(acc).reduce(
      (sortedAcc, [parentTaskId, subtasks]) => ({
        ...sortedAcc,
        [parentTaskId]: sortReadinessTasks(subtasks),
      }),
      {},
    );
  }, {});

const mergeReadinessAppointmentsBody = (currentBody, nextBody) => {
  if (!currentBody) return nextBody;
  if (!nextBody) return currentBody;

  const appointmentsById = new Map(
    (currentBody?.data || []).map((appointment) => [
      `${appointment?.id || ''}`.trim(),
      appointment,
    ]),
  );
  (nextBody?.data || []).forEach((appointment) => {
    const appointmentId = `${appointment?.id || ''}`.trim();
    if (appointmentId) appointmentsById.set(appointmentId, appointment);
  });

  const currentAppointmentTasksById =
    currentBody?.appointmentTasksById ?? currentBody?.appointment_tasks_by_id ?? {};
  const nextAppointmentTasksById =
    nextBody?.appointmentTasksById ?? nextBody?.appointment_tasks_by_id ?? {};

  return {
    ...currentBody,
    ...nextBody,
    data: Array.from(appointmentsById.values()),
    appointmentTasksById: {
      ...currentAppointmentTasksById,
      ...nextAppointmentTasksById,
    },
  };
};

const getReadinessWindowsByKey = (windowKeys = []) => {
  const requestedWindowKeySet = new Set(
    Array.from(windowKeys)
      .map((windowKey) => `${windowKey || ''}`.trim())
      .filter(Boolean),
  );
  const matchedWindows = READINESS_APPOINTMENT_WINDOWS.filter((readinessWindow) =>
    requestedWindowKeySet.has(readinessWindow.key),
  );

  return matchedWindows.length ? matchedWindows : [INITIAL_READINESS_APPOINTMENT_WINDOW];
};

const collectDelegatedSubtaskParentIds = ({
  taskQueueRows = [],
  readinessAppointmentTasksById = {},
}) => {
  const parentTaskIds = new Set();

  const registerTask = (task) => {
    const taskId = `${task?.id || ''}`.trim();
    const parentTaskId = `${task?.parentTaskId || ''}`.trim();

    if (parentTaskId) {
      parentTaskIds.add(parentTaskId);
      return;
    }

    if (taskId) {
      parentTaskIds.add(taskId);
    }
  };

  taskQueueRows.forEach(registerTask);
  Object.values(readinessAppointmentTasksById).flat().forEach(registerTask);

  return Array.from(parentTaskIds);
};

const getAppointmentTaskLookupKey = (appointment) => {
  const sourceAppointmentId = `${appointment?.sourceAppointmentId || ''}`.trim();
  const appointmentId = `${appointment?.id || ''}`.trim();
  const workflowInstanceId = `${appointment?.workflowInstanceId || ''}`.trim();
  const taskAppointmentId = sourceAppointmentId || appointmentId;
  if (taskAppointmentId && workflowInstanceId) {
    return getAppointmentWorkflowKey(taskAppointmentId, workflowInstanceId);
  }

  return appointmentId;
};

const isTaskFollowedByCurrentUser = (task, currentUserId) => {
  const normalizedCurrentUserId = `${currentUserId ?? ''}`.trim();
  if (!normalizedCurrentUserId) return false;

  return (task?.followedBy || []).some(
    (followerId) => `${followerId ?? ''}`.trim() === normalizedCurrentUserId,
  );
};

const mergeTaskRowsById = (currentRows = [], nextRows = []) => {
  const mergedRowsById = new Map();

  [...currentRows, ...nextRows].forEach((task) => {
    const taskId = `${task?.id ?? ''}`.trim();
    if (!taskId) return;
    mergedRowsById.set(taskId, task);
  });

  return Array.from(mergedRowsById.values());
};

const getToastedTaskNotificationIds = () => {
  if (typeof window === 'undefined') return new Set();

  try {
    const raw = window.localStorage.getItem(TASK_NOTIFICATION_TOASTED_IDS_STORAGE_KEY);
    if (!raw) return new Set();

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();

    return new Set(parsed);
  } catch {
    return new Set();
  }
};

const saveToastedTaskNotificationIds = (ids) => {
  if (typeof window === 'undefined') return;

  try {
    const normalizedIds = Array.from(ids).slice(-TASK_NOTIFICATION_TOASTED_IDS_MAX);
    window.localStorage.setItem(
      TASK_NOTIFICATION_TOASTED_IDS_STORAGE_KEY,
      JSON.stringify(normalizedIds),
    );
  } catch {
    console.error('Failed to save toasted task notification IDs to localStorage');
  }
};

export default function MindspanCareOrchestration() {
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { showAlert, AlertType } = useAlert();
  const persistedState = useCustomSelector(getCareOrchestrationState);

  const [ownerFilter, setOwnerFilter] = useState(persistedState?.ownerFilter || 'all');
  const [activeTeam, setActiveTeam] = useState(persistedState?.activeTeam || 'all');
  const [statusFilter, setStatusFilter] = useState(persistedState?.statusFilter || 'open');
  const [patientSearch, setPatientSearch] = useState(persistedState?.patientSearch || null);
  const [taskSortMode, setTaskSortMode] = useState(
    persistedState?.taskSortMode || TASK_QUEUE_SORT_MODE.PatientPriority,
  );
  const [organizationScope, setOrganizationScope] = useState(() => {
    const state = store.getState();
    const userId = getCurrentUserId(state);
    const user = singleModelSelector(state, 'Client', userId);
    if (!user?.defaultPlatform) return null;
    return getPlatformScope(user.defaultPlatform);
  });
  const [isSettingDefaultOrgScope, setIsSettingDefaultOrgScope] = useState(false);
  const [workflowScope, setWorkflowScope] = useState(WORKFLOW_SCOPE.PRE_VISIT);
  const [sortKey, setSortKey] = useState(persistedState?.sortKey || 'priority');
  const [sortDirection, setSortDirection] = useState(persistedState?.sortDirection || 'asc');
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [selectedPostVisitAppt, setSelectedPostVisitAppt] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [taskFromReadiness, setTaskFromReadiness] = useState(null);
  const [delegatedSubtasksByParentId, setDelegatedSubtasksByParentId] = useState({});
  const [expandedPatients, setExpandedPatients] = useState(persistedState?.expandedPatients || {});
  const [assigningTaskIds, setAssigningTaskIds] = useState(new Set());
  const [unassigningTaskId, setUnassigningTaskId] = useState(null);
  const [markingTaskId, setMarkingTaskId] = useState(null);
  const [cancelingTaskId, setCancelingTaskId] = useState(null);
  const [reassigningTaskId, setReassigningTaskId] = useState(null);
  const [followingTaskId, setFollowingTaskId] = useState(null);
  const [unfollowingTaskId, setUnfollowingTaskId] = useState(null);
  const [blockingTaskId, setBlockingTaskId] = useState(null);
  const [unblockingTaskId, setUnblockingTaskId] = useState(null);
  const [escalatingTaskId, setEscalatingTaskId] = useState(null);
  const [deescalatingTaskId, setDeescalatingTaskId] = useState(null);
  const [delegatingSubtasksTaskId, setDelegatingSubtasksTaskId] = useState(null);
  const [cancelingSubtaskId, setCancelingSubtaskId] = useState(null);
  const [sessionCompletedTaskIds, setSessionCompletedTaskIds] = useState({});
  const [taskQueueRows, setTaskQueueRows] = useState([]);
  const [leadTaskCount, setLeadTaskCount] = useState(0);
  const [taskQueueMeta, setTaskQueueMeta] = useState(null);
  const [readinessSourceAppointments, setReadinessSourceAppointments] = useState([]);
  const [readinessAppointmentTasksById, setReadinessAppointmentTasksById] = useState({});
  const [readinessDataReady, setReadinessDataReady] = useState(false);
  const [postVisitAppointments, setPostVisitAppointments] = useState([]);
  const [postVisitAppointmentTasksById, setPostVisitAppointmentTasksById] = useState({});
  const [postVisitDataReady, setPostVisitDataReady] = useState(false);
  const [dismissedPostVisitApptIds, setDismissedPostVisitApptIds] = useState(() => new Set());
  const [isLoadingMoreTasks, setIsLoadingMoreTasks] = useState(false);
  const [loadedReadinessWindowKeys, setLoadedReadinessWindowKeys] = useState(new Set());
  const [isExpandingTaskSearch, setIsExpandingTaskSearch] = useState(false);
  const taskQueueRowsRef = useRef([]);
  const capacityTasksRef = useRef([]);
  const taskQueueMetaRef = useRef(null);
  const taskQueueMetaFilterKeyRef = useRef(null);
  const readinessAppointmentTasksByIdRef = useRef({});
  const loadedReadinessWindowKeysRef = useRef(new Set());
  const isLoadingMoreReadinessAppointmentsRef = useRef(false);
  const delegatedSubtasksByParentIdRef = useRef({});
  const hydratedDelegatedSubtaskParentIdsRef = useRef(new Set());
  const delegatedSubtasksRequestIdRef = useRef(0);
  const ownerFilterRef = useRef(ownerFilter);
  const activeTeamRef = useRef(activeTeam);
  const statusFilterRef = useRef(statusFilter);
  const patientSearchRef = useRef(patientSearch);
  const taskSortModeRef = useRef(taskSortMode);
  const organizationScopeRef = useRef(organizationScope);
  const workflowScopeRef = useRef(workflowScope);
  const postVisitReadinessBodyRef = useRef(null);
  const isLoadingPostVisitReadinessRef = useRef(false);
  const postVisitOrganizationScopeRef = useRef(organizationScope);
  const taskQueueCacheRef = useRef({});
  const firstPageTaskQueueByFilterKeyRef = useRef({});
  const taskQueueRequestKeyRef = useRef(null);
  const taskQueueMetaRequestRef = useRef(null);
  const supplementalDataRef = useRef({
    appointmentsBody: null,
    readinessAppointmentsBody: null,
    statsBody: null,
  });
  const [readinessPinnedTeam, setReadinessPinnedTeam] = useState(null);
  const [pinnedDepartmentScope, setPinnedDepartmentScope] = useState(null);
  const [scopeFilters, setScopeFiltersState] = useState(EMPTY_SCOPE_FILTERS);
  const [guideEnabled, setGuideEnabledState] = useState(false);
  const [isScopePaused, setIsScopePaused] = useState(false);
  const { departments: athenaDepartments, departmentLabelById } = useAthenaDepartments();
  const departmentDefinitions = useMemo(
    () => athenaDepartments.filter((department) => !isTestAthenaDepartment(department)),
    [athenaDepartments],
  );
  const scopeDepartmentAthenaIds = useMemo(() => {
    if (!hasActiveScopeFilters(scopeFilters) && !guideEnabled) return [];
    return departmentsInScope(departmentDefinitions, scopeFilters, guideEnabled).map((d) => d.id);
  }, [departmentDefinitions, scopeFilters, guideEnabled]);
  const isAllScope = isScopePaused || scopeDepartmentAthenaIds.length === 0;
  const activeScopeDepartmentAthenaIds = isAllScope ? [] : scopeDepartmentAthenaIds;
  const departmentScopeRef = useRef(activeScopeDepartmentAthenaIds);
  useEffect(() => {
    departmentScopeRef.current = activeScopeDepartmentAthenaIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScopeDepartmentAthenaIds.join('|')]);

  const setScopeFilters = useCallback((nextFilters) => {
    setScopeFiltersState(nextFilters);
    setIsScopePaused(false);
  }, []);
  const setGuideEnabled = useCallback((nextGuideEnabled) => {
    setGuideEnabledState(nextGuideEnabled);
    setIsScopePaused(false);
  }, []);

  const hasAppliedInitialPinnedScopeRef = useRef(false);
  useEffect(() => {
    if (hasAppliedInitialPinnedScopeRef.current || !pinnedDepartmentScope) return;
    hasAppliedInitialPinnedScopeRef.current = true;
    setScopeFiltersState({
      regions: pinnedDepartmentScope.regions,
      counties: pinnedDepartmentScope.counties,
      markets: pinnedDepartmentScope.markets,
      departments: pinnedDepartmentScope.departments,
    });
    setGuideEnabledState(Boolean(pinnedDepartmentScope.guideEnabled));
  }, [pinnedDepartmentScope]);

  const isPinnedToCurrentScope = filtersEqualToPinnedScope(
    scopeFilters,
    guideEnabled,
    pinnedDepartmentScope,
  );
  const canResetToPinnedScope = Boolean(pinnedDepartmentScope) && !isPinnedToCurrentScope;
  const showScopeUtilities =
    hasActiveScopeFilters(scopeFilters) || guideEnabled || Boolean(pinnedDepartmentScope);
  const [taskNotificationPreferences, setTaskNotificationPreferences] = useState(null);
  const [isTaskNotificationPreferencesLoading, setIsTaskNotificationPreferencesLoading] =
    useState(true);
  const [isTaskNotificationPreferencesSaving, setIsTaskNotificationPreferencesSaving] =
    useState(false);
  const [taskNotifications, setTaskNotifications] = useState([]);
  const [isTaskNotificationsLoading, setIsTaskNotificationsLoading] = useState(true);
  const [taskNotificationToasts, setTaskNotificationToasts] = useState([]);
  const [bulkAssignToast, setBulkAssignToast] = useState(null);
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState(
    getTaskBrowserNotificationPermission(),
  );

  const unreadTaskNotificationsCount =
    taskNotificationPreferences?.notificationsEnabled &&
    isTaskNotificationChannelEnabled(taskNotificationPreferences, 'inApp')
      ? taskNotifications.filter((notification) => {
          if (notification?.status !== NOTIFICATION_STATUS.Sent) return false;

          const categoryKey = NOTIFICATION_CATEGORY_KEY_BY_ID[notification?.category];
          if (!categoryKey) return false;

          return !!taskNotificationPreferences?.categories?.[categoryKey]?.inApp;
        }).length
      : 0;

  const careOrchestrationDocumentTitle = unreadTaskNotificationsCount
    ? `(${unreadTaskNotificationsCount}) Orchestration Manager`
    : 'Orchestration Manager';

  const taskNotificationsStreamRef = useRef(null);
  const showAlertRef = useRef(showAlert);
  useEffect(() => {
    showAlertRef.current = showAlert;
  }, [showAlert]);

  const loadTaskNotifications = useCallback(async () => {
    setIsTaskNotificationsLoading(true);

    try {
      const response = await getMyTaskNotifications();

      setTaskNotifications(
        (response || []).filter(
          (notification) =>
            notification?.status !== NOTIFICATION_STATUS.Dismissed &&
            notification?.status !== NOTIFICATION_STATUS.Pending,
        ),
      );
    } catch (error) {
      setTaskNotifications([]);
      showAlertRef.current(
        AlertType.Error,
        '',
        error?.message || 'Failed to load task notifications',
      );
    } finally {
      setIsTaskNotificationsLoading(false);
    }
  }, [AlertType.Error]);

  const getVisibleInAppTaskNotifications = (notifications, preferences) => {
    if (!preferences?.notificationsEnabled) return [];
    if (!isTaskNotificationChannelEnabled(preferences, 'inApp')) return [];

    return (notifications || []).filter((notification) => {
      const categoryKey = NOTIFICATION_CATEGORY_KEY_BY_ID[notification?.category];
      if (!categoryKey) return false;

      return !!preferences?.categories?.[categoryKey]?.inApp;
    });
  };

  const getVisibleBrowserTaskNotifications = (notifications, preferences) => {
    if (!preferences?.notificationsEnabled) return [];
    if (!isTaskNotificationChannelEnabled(preferences, 'browser')) return [];

    return (notifications || []).filter((notification) => {
      const categoryKey = NOTIFICATION_CATEGORY_KEY_BY_ID[notification?.category];
      if (!categoryKey) return false;

      return !!preferences?.categories?.[categoryKey]?.browser;
    });
  };

  const appliedTaskRouteKeyRef = useRef(null);
  const shownTaskNotificationToastIdsRef = useRef(getToastedTaskNotificationIds());
  const shownBrowserNotificationIdsRef = useRef(new Set());
  const lastTaskQueueSyncAtRef = useRef(null);
  const isRefreshingSupplementalDataRef = useRef(false);
  const shouldRefreshSupplementalDataAgainRef = useRef(false);
  const supplementalDataRequestRef = useRef(null);
  const statsDataRequestRef = useRef(null);

  const handleTaskNotificationCreated = useCallback(async () => {
    await loadTaskNotifications();
  }, [loadTaskNotifications]);

  const handleTaskNotificationsStreamConnected = useCallback(() => {
    console.info('Task notifications stream connected');
  }, []);

  const handleTaskNotificationsStreamError = useCallback(() => {
    console.error('Task notifications stream error');
  }, []);

  const getReadinessSupplementalParams = useCallback(
    (readinessWindow = INITIAL_READINESS_APPOINTMENT_WINDOW) => {
      const { dateFrom, dateTo } = getReadinessWindowDateRange(readinessWindow);

      return {
        organizationScope: organizationScopeRef.current,
        readinessDateFrom: dateFrom,
        readinessDateTo: dateTo,
      };
    },
    [],
  );

  const fetchSupplementalDataOnce = useCallback((params = {}) => {
    const requestKey = JSON.stringify(params || {});
    if (
      !supplementalDataRequestRef.current ||
      supplementalDataRequestRef.current.key !== requestKey
    ) {
      const promise = fetchDashboardSupplementalData(params).finally(() => {
        if (supplementalDataRequestRef.current?.key === requestKey) {
          supplementalDataRequestRef.current = null;
        }
      });
      supplementalDataRequestRef.current = { key: requestKey, promise };
    }

    return supplementalDataRequestRef.current.promise;
  }, []);

  const fetchReadinessAppointmentsForLoadedWindows = useCallback(
    async (windowKeys = loadedReadinessWindowKeysRef.current) => {
      const readinessWindows = getReadinessWindowsByKey(windowKeys);
      const readinessResponses = await Promise.all(
        readinessWindows.map((readinessWindow) =>
          fetchReadinessAppointmentsData(getReadinessSupplementalParams(readinessWindow)),
        ),
      );

      return {
        readinessAppointmentsBody: readinessResponses.reduce(
          (mergedBody, responseBody) => mergeReadinessAppointmentsBody(mergedBody, responseBody),
          null,
        ),
        loadedWindowKeys: new Set(readinessWindows.map((readinessWindow) => readinessWindow.key)),
      };
    },
    [getReadinessSupplementalParams],
  );

  const fetchStatsDataOnce = useCallback(() => {
    if (!statsDataRequestRef.current) {
      statsDataRequestRef.current = fetchDashboardStatsData().finally(() => {
        statsDataRequestRef.current = null;
      });
    }

    return statsDataRequestRef.current;
  }, []);

  const tasks = useCustomSelector(getDashboardV2Tasks);
  const currentUserId = useCustomSelector(getCurrentUserId);
  const currentUser = useCustomSelector((state) =>
    singleModelSelector(state, 'Client', currentUserId),
  );
  const capacityTasks = useCustomSelector(getCareOrchestrationCapacityTasks);
  const defaultOrgScope = currentUser?.defaultPlatform
    ? getPlatformScope(currentUser.defaultPlatform)
    : OrgScope.Perry;
  // Cortex's corpus is Mindspan SOPs, so Perry tasks get no Ask Cortex card.
  const cortexOrigin = organizationScope === OrgScope.Mindspan ? CORTEX_ORIGIN : null;
  const isCareOrchestrationLoading = useCustomSelector(getCareOrchestrationIsLoading);
  const careOrchestrationMeta = useCustomSelector(getCareOrchestrationMeta);
  const careOrchestrationMetaRef = useRef(careOrchestrationMeta);
  const appointments = useCustomSelector(getDashboardV2Appointments);
  // Flags arrive with the initial app data; until then every isFeatureEnabled is false.
  const featureFlagsLoaded = useCustomSelector((state) => getFeatureFlags(state).length > 0);
  const isReadinessEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'careOrchestrationReadiness'),
  );
  const isTasksEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'careOrchestrationTasks'),
  );
  const isStreamSSEEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'taskNotificationsStream'),
  );

  const closeTaskNotificationsStream = useCallback(() => {
    if (taskNotificationsStreamRef.current) {
      taskNotificationsStreamRef.current.removeEventListener(
        'connected',
        handleTaskNotificationsStreamConnected,
      );
      taskNotificationsStreamRef.current.removeEventListener(
        'task-notification-created',
        handleTaskNotificationCreated,
      );
      taskNotificationsStreamRef.current.onerror = null;
      taskNotificationsStreamRef.current.close();
      taskNotificationsStreamRef.current = null;
    }
  }, [handleTaskNotificationCreated, handleTaskNotificationsStreamConnected]);

  const openTaskNotificationsStream = useCallback(() => {
    closeTaskNotificationsStream();
    if (!isStreamSSEEnabled) return;

    const streamUrl = getMyTaskNotificationsStreamUrl();
    const stream = new EventSource(streamUrl, { withCredentials: true });

    stream.addEventListener('connected', handleTaskNotificationsStreamConnected);
    stream.addEventListener('task-notification-created', handleTaskNotificationCreated);
    stream.onerror = handleTaskNotificationsStreamError;

    taskNotificationsStreamRef.current = stream;
  }, [
    closeTaskNotificationsStream,
    handleTaskNotificationCreated,
    handleTaskNotificationsStreamConnected,
    handleTaskNotificationsStreamError,
    isStreamSSEEnabled,
  ]);
  const currentUserName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
  const currentUserNameLower = currentUserName.toLowerCase();

  const applyReadinessSync = useCallback(
    (responseBody, { append = false, setAppointments, setTasksById, tasksByIdRef, onReady }) => {
      const normalizedAppointments = (responseBody?.data || []).map(normalizeAppointment);
      const normalizedAppointmentTasksById = normalizeReadinessAppointmentTasksById(responseBody);
      const normalizedSubtasksByParentId =
        normalizeReadinessAppointmentSubtasksByParentId(responseBody);

      syncEmbeddedIntegrations(dispatch, [], responseBody?.data || []);

      setAppointments((prev) => {
        if (!append) return normalizedAppointments;

        const appointmentsById = new Map(
          prev.map((appointment) => [`${appointment?.id || ''}`.trim(), appointment]),
        );
        normalizedAppointments.forEach((appointment) => {
          const appointmentId = `${appointment?.id || ''}`.trim();
          if (appointmentId) appointmentsById.set(appointmentId, appointment);
        });

        return Array.from(appointmentsById.values());
      });
      setTasksById((prev) => {
        const nextState = append ? { ...prev } : {};
        Object.entries(normalizedAppointmentTasksById).forEach(([lookupKey, appointmentTasks]) => {
          nextState[lookupKey] = appointmentTasks;
        });
        if (tasksByIdRef) tasksByIdRef.current = nextState;
        return nextState;
      });
      if (Object.keys(normalizedSubtasksByParentId).length > 0) {
        setDelegatedSubtasksByParentId((prev) => {
          const nextState = { ...prev };
          Object.entries(normalizedSubtasksByParentId).forEach(([parentTaskId, subtasks]) => {
            nextState[parentTaskId] = sortReadinessTasks(
              mergeTaskRowsById(nextState[parentTaskId] || [], subtasks),
            );
          });
          delegatedSubtasksByParentIdRef.current = nextState;
          return nextState;
        });
      }
      onReady();
    },
    [dispatch],
  );

  const syncReadinessAppointmentsState = useCallback(
    (responseBody, { append = false } = {}) =>
      applyReadinessSync(responseBody, {
        append,
        setAppointments: setReadinessSourceAppointments,
        setTasksById: setReadinessAppointmentTasksById,
        tasksByIdRef: readinessAppointmentTasksByIdRef,
        onReady: () => setReadinessDataReady(true),
      }),
    [applyReadinessSync],
  );

  const syncPostVisitReadinessState = useCallback(
    (responseBody) =>
      applyReadinessSync(responseBody, {
        setAppointments: setPostVisitAppointments,
        setTasksById: setPostVisitAppointmentTasksById,
        onReady: () => setPostVisitDataReady(true),
      }),
    [applyReadinessSync],
  );

  const applyReadinessTaskUpdater = useCallback(
    (tasksById, updater) =>
      Object.entries(tasksById).reduce((acc, [lookupKey, appointmentTasks]) => {
        acc[lookupKey] = sortReadinessTasks(
          appointmentTasks.map((taskRow) => {
            if (typeof updater === 'function') {
              return updater(taskRow);
            }

            if (updater?.id && taskRow?.id === updater.id) {
              return {
                ...taskRow,
                ...normalizeDashboardV2Task(updater),
              };
            }

            return taskRow;
          }),
        );
        return acc;
      }, {}),
    [],
  );

  const syncLocalReadinessTaskRows = useCallback(
    (updater) => {
      setReadinessAppointmentTasksById((prev) => {
        const nextState = applyReadinessTaskUpdater(prev, updater);
        readinessAppointmentTasksByIdRef.current = nextState;
        return nextState;
      });
      setPostVisitAppointmentTasksById((prev) => applyReadinessTaskUpdater(prev, updater));
    },
    [applyReadinessTaskUpdater],
  );

  const removeLocalReadinessTaskRows = useCallback((taskIds = []) => {
    const removedTaskIdSet = new Set(
      taskIds.map((taskId) => `${taskId || ''}`.trim()).filter(Boolean),
    );
    if (!removedTaskIdSet.size) return;

    const removeFromTasksById = (tasksById) =>
      Object.entries(tasksById).reduce((acc, [lookupKey, appointmentTasks]) => {
        acc[lookupKey] = appointmentTasks.filter(
          (taskRow) => !removedTaskIdSet.has(`${taskRow?.id || ''}`.trim()),
        );
        return acc;
      }, {});

    setReadinessAppointmentTasksById((prev) => {
      const nextState = removeFromTasksById(prev);
      readinessAppointmentTasksByIdRef.current = nextState;
      return nextState;
    });
    setPostVisitAppointmentTasksById((prev) => removeFromTasksById(prev));
  }, []);

  const syncDelegatedSubtasksState = useCallback((updater, hydratedParentTaskIds = []) => {
    setDelegatedSubtasksByParentId((prev) => {
      const nextState = typeof updater === 'function' ? updater(prev) : updater;
      delegatedSubtasksByParentIdRef.current = nextState;

      if (Array.isArray(hydratedParentTaskIds) && hydratedParentTaskIds.length) {
        const nextHydratedParentTaskIds = new Set(hydratedDelegatedSubtaskParentIdsRef.current);
        hydratedParentTaskIds.forEach((taskId) => {
          const normalizedTaskId = `${taskId || ''}`.trim();
          if (normalizedTaskId) {
            nextHydratedParentTaskIds.add(normalizedTaskId);
          }
        });
        hydratedDelegatedSubtaskParentIdsRef.current = nextHydratedParentTaskIds;
      }

      return nextState;
    });
  }, []);

  const hydrateDelegatedSubtasks = useCallback(
    async ({
      taskQueueRows: nextTaskQueueRows = taskQueueRowsRef.current,
      readinessAppointmentTasksById:
        nextReadinessAppointmentTasksById = readinessAppointmentTasksByIdRef.current,
    } = {}) => {
      const parentTaskIds = collectDelegatedSubtaskParentIds({
        taskQueueRows: nextTaskQueueRows,
        readinessAppointmentTasksById: nextReadinessAppointmentTasksById,
      });

      const requestId = delegatedSubtasksRequestIdRef.current + 1;
      delegatedSubtasksRequestIdRef.current = requestId;

      if (!parentTaskIds.length) {
        return;
      }

      const missingParentTaskIds = parentTaskIds.filter(
        (taskId) => !hydratedDelegatedSubtaskParentIdsRef.current.has(taskId),
      );

      if (!missingParentTaskIds.length) {
        return;
      }

      try {
        const subtasksByParentId = await fetchTaskSubtasksBulk(missingParentTaskIds);
        if (delegatedSubtasksRequestIdRef.current !== requestId) return;

        syncDelegatedSubtasksState(
          (prev) => ({
            ...prev,
            ...Object.fromEntries(missingParentTaskIds.map((taskId) => [taskId, []])),
            ...subtasksByParentId,
          }),
          missingParentTaskIds,
        );
      } catch {
        // Ignore subtask hydration failures; a later refresh can reconcile them.
      }
    },
    [syncDelegatedSubtasksState],
  );

  useDocumentTitle(careOrchestrationDocumentTitle);

  const availableTabs = useMemo(
    () => [
      { key: TAB_KEYS.TASKS, enabled: isTasksEnabled },
      { key: TAB_KEYS.LEADS, enabled: isTasksEnabled },
      { key: TAB_KEYS.READINESS, enabled: isReadinessEnabled },
      { key: TAB_KEYS.BULK_ASSIGN, enabled: isTasksEnabled },
    ],
    [isReadinessEnabled, isTasksEnabled],
  );

  const firstEnabledTab = useMemo(
    () => availableTabs.find((tab) => tab.enabled)?.key ?? null,
    [availableTabs],
  );

  const hasEnabledTabs = !!firstEnabledTab;
  const requestedRoute = useMemo(
    () => parseCareOrchestrationRoute(location.pathname),
    [location.pathname],
  );
  const requestedView = requestedRoute.view;
  const view = useMemo(() => {
    const enabledViews = availableTabs.filter((tab) => tab.enabled).map((tab) => tab.key);

    if (!enabledViews.length) return null;
    if (requestedView && enabledViews.includes(requestedView)) return requestedView;
    return firstEnabledTab;
  }, [availableTabs, firstEnabledTab, requestedView]);

  const availableTasks = useMemo(
    () =>
      tasks.filter(
        (task) => sessionCompletedTaskIds[task.id] || !shouldHideTaskByCallbackStatus(task),
      ),
    [sessionCompletedTaskIds, tasks],
  );

  const tasksById = useMemo(
    () => availableTasks.reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [availableTasks],
  );
  const allTasksById = useMemo(
    () => tasks.reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [tasks],
  );

  const appointmentTasksById = readinessAppointmentTasksById;
  const readinessAppointments = useMemo(
    () => readinessSourceAppointments,
    [readinessSourceAppointments],
  );
  const appointmentsById = useMemo(
    () =>
      readinessAppointments.reduce(
        (acc, appointment) => ({ ...acc, [appointment.id]: appointment }),
        {},
      ),
    [readinessAppointments],
  );
  const sortedAppts = useMemo(
    () => sortAppointmentsByDate(readinessAppointments),
    [readinessAppointments],
  );
  const apptsByDate = useMemo(() => groupAppointmentsByDate(sortedAppts), [sortedAppts]);
  const visiblePostVisitAppointments = useMemo(
    () => postVisitAppointments.filter((appt) => !dismissedPostVisitApptIds.has(`${appt?.id}`)),
    [postVisitAppointments, dismissedPostVisitApptIds],
  );
  const postApptsByDate = useMemo(
    () => groupAppointmentsByDate(sortAppointmentsByDate(visiblePostVisitAppointments)),
    [visiblePostVisitAppointments],
  );
  const appointmentTaskLookupById = useMemo(
    () =>
      [
        ...Object.values(appointmentTasksById),
        ...Object.values(postVisitAppointmentTasksById),
        ...Object.values(delegatedSubtasksByParentId),
      ]
        .flat()
        .reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [appointmentTasksById, postVisitAppointmentTasksById, delegatedSubtasksByParentId],
  );
  useEffect(() => {
    taskQueueRowsRef.current = taskQueueRows;
  }, [taskQueueRows]);

  useEffect(() => {
    capacityTasksRef.current = capacityTasks;
  }, [capacityTasks]);

  useEffect(() => {
    taskQueueMetaRef.current = taskQueueMeta;
  }, [taskQueueMeta]);

  useEffect(() => {
    careOrchestrationMetaRef.current = careOrchestrationMeta;
  }, [careOrchestrationMeta]);

  const setCapacityTasksState = useCallback(
    (nextCapacityTasks) => {
      dispatch(
        setCareOrchestrationCapacityTasks(
          (nextCapacityTasks || []).map((task) => normalizeDashboardV2Task(task)),
        ),
      );
    },
    [dispatch],
  );

  const upsertCapacityTaskFromResponse = useCallback(
    (taskRow) => {
      if (!taskRow?.id) return;

      const normalizedTask = normalizeDashboardV2Task(taskRow);
      const normalizedOwner = `${normalizedTask?.owner || ''}`.trim().toLowerCase();
      const shouldInclude = !!currentUserNameLower && normalizedOwner === currentUserNameLower;
      const currentCapacityTasks = capacityTasksRef.current || [];
      const existingIndex = currentCapacityTasks.findIndex(
        (capacityTask) => `${capacityTask?.id || ''}` === `${normalizedTask.id}`,
      );

      if (!shouldInclude) {
        if (existingIndex === -1) return;
        setCapacityTasksState(
          currentCapacityTasks.filter(
            (capacityTask) => `${capacityTask?.id || ''}` !== `${normalizedTask.id}`,
          ),
        );
        return;
      }

      if (existingIndex === -1) {
        setCapacityTasksState([...currentCapacityTasks, normalizedTask]);
        return;
      }

      const nextCapacityTasks = [...currentCapacityTasks];
      nextCapacityTasks[existingIndex] = normalizedTask;
      setCapacityTasksState(nextCapacityTasks);
    },
    [currentUserNameLower, setCapacityTasksState],
  );

  const removeCapacityTasksById = useCallback(
    (taskIds = []) => {
      if (!taskIds.length) return;
      const removeIds = new Set(taskIds.map((taskId) => `${taskId || ''}`).filter(Boolean));
      if (!removeIds.size) return;

      setCapacityTasksState(
        (capacityTasksRef.current || []).filter(
          (capacityTask) => !removeIds.has(`${capacityTask?.id || ''}`),
        ),
      );
    },
    [setCapacityTasksState],
  );

  useEffect(() => {
    readinessAppointmentTasksByIdRef.current = readinessAppointmentTasksById;
  }, [readinessAppointmentTasksById]);

  useEffect(() => {
    ownerFilterRef.current = ownerFilter;
  }, [ownerFilter]);

  useEffect(() => {
    activeTeamRef.current = activeTeam;
  }, [activeTeam]);

  useEffect(() => {
    statusFilterRef.current = statusFilter;
  }, [statusFilter]);

  useEffect(() => {
    patientSearchRef.current = patientSearch;
  }, [patientSearch]);

  useEffect(() => {
    taskSortModeRef.current = taskSortMode;
  }, [taskSortMode]);

  useEffect(() => {
    organizationScopeRef.current = organizationScope;
  }, [organizationScope]);

  useEffect(() => {
    workflowScopeRef.current = workflowScope;
  }, [workflowScope]);

  useEffect(() => {
    if (!currentUser?.defaultPlatform) return;
    if (organizationScopeRef.current === defaultOrgScope) return;
    setOrganizationScope(defaultOrgScope);
    organizationScopeRef.current = defaultOrgScope;
  }, [currentUser?.defaultPlatform]);

  const getTaskQueueFilterCacheKey = useCallback(
    ({
      sortMode = taskSortModeRef.current,
      team = activeTeamRef.current,
      ownerScope = ownerFilterRef.current,
      statusScope = statusFilterRef.current,
      orgScope = organizationScopeRef.current,
      deptScope = departmentScopeRef.current,
    } = {}) =>
      [sortMode, team, ownerScope, statusScope, orgScope, [...deptScope].sort().join(',')].join(
        '|',
      ),
    [],
  );

  const shouldHydrateEntireTaskQueue = useCallback(
    (
      sortMode = taskSortModeRef.current,
      ownerScope = ownerFilterRef.current,
      statusScope = statusFilterRef.current,
    ) =>
      sortMode === TASK_QUEUE_SORT_MODE.ReasonPriority ||
      sortMode === TASK_QUEUE_SORT_MODE.Priority ||
      (ownerScope === 'my' && statusScope === 'open'),
    [],
  );

  const storeTaskQueueCache = useCallback(
    (cacheKey, nextTaskQueueRows, nextTaskQueueMeta, cacheAsFirstPage = false) => {
      taskQueueCacheRef.current[cacheKey] = {
        taskQueueRows: nextTaskQueueRows,
        taskQueueMeta: nextTaskQueueMeta,
      };

      if (cacheAsFirstPage) {
        firstPageTaskQueueByFilterKeyRef.current[cacheKey] = {
          taskQueueRows: nextTaskQueueRows,
          taskQueueMeta: nextTaskQueueMeta,
        };
      }
    },
    [],
  );

  const invalidateTaskQueueCaches = useCallback(() => {
    taskQueueCacheRef.current = {};
    firstPageTaskQueueByFilterKeyRef.current = {};
    hydratedDelegatedSubtaskParentIdsRef.current = new Set();
  }, []);

  const syncTaskQueueMetaState = useCallback(
    (
      nextTaskQueueMeta,
      { invalidateCaches = false, filterCacheKey = getTaskQueueFilterCacheKey() } = {},
    ) => {
      if (!nextTaskQueueMeta) return;

      if (invalidateCaches) {
        invalidateTaskQueueCaches();
      }

      taskQueueMetaRef.current = nextTaskQueueMeta;
      taskQueueMetaFilterKeyRef.current = filterCacheKey;
      setTaskQueueMeta(nextTaskQueueMeta);
      lastTaskQueueSyncAtRef.current =
        nextTaskQueueMeta?.serverTime || lastTaskQueueSyncAtRef.current || new Date().toISOString();
      const currentMeta = careOrchestrationMetaRef.current;

      dispatch(
        setCareOrchestrationMeta({
          tasks: nextTaskQueueMeta,
          appointments:
            currentMeta?.appointments || supplementalDataRef.current.appointmentsBody?.meta || null,
          stats: currentMeta?.stats || supplementalDataRef.current.statsBody?.meta || null,
          readiness:
            currentMeta?.readiness ||
            supplementalDataRef.current.readinessAppointmentsBody?.meta ||
            null,
        }),
      );
    },
    [dispatch, getTaskQueueFilterCacheKey, invalidateTaskQueueCaches],
  );

  const refreshTaskQueueMetaData = useCallback(
    async ({
      sortMode = taskSortModeRef.current,
      team = activeTeamRef.current,
      ownerScope = ownerFilterRef.current,
      statusScope = statusFilterRef.current,
      orgScope = organizationScopeRef.current,
      deptScope = departmentScopeRef.current,
      filterCacheKey = getTaskQueueFilterCacheKey({
        sortMode,
        team,
        ownerScope,
        statusScope,
        orgScope,
        deptScope,
      }),
    } = {}) => {
      if (
        taskQueueMetaFilterKeyRef.current === filterCacheKey &&
        hasFullTaskQueueMeta(taskQueueMetaRef.current)
      ) {
        return taskQueueMetaRef.current;
      }

      if (taskQueueMetaRequestRef.current?.key === filterCacheKey) {
        return taskQueueMetaRequestRef.current.promise;
      }

      const promise = fetchTaskQueueMetaData({
        pageSize: TASK_QUEUE_PAGE_SIZE,
        offset: 0,
        team,
        ownerScope,
        statusScope,
        organizationScope: orgScope,
        departmentAthenaIds: deptScope,
      })
        .then((nextTaskQueueMeta) => {
          if (filterCacheKey !== getTaskQueueFilterCacheKey()) {
            return null;
          }

          syncTaskQueueMetaState(nextTaskQueueMeta, { filterCacheKey });
          return nextTaskQueueMeta;
        })
        .finally(() => {
          if (taskQueueMetaRequestRef.current?.key === filterCacheKey) {
            taskQueueMetaRequestRef.current = null;
          }
        });

      taskQueueMetaRequestRef.current = { key: filterCacheKey, promise };
      return promise;
    },
    [getTaskQueueFilterCacheKey, syncTaskQueueMetaState],
  );

  const syncTaskQueueState = useCallback(
    ({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      appointmentsBody = undefined,
      readinessAppointmentsBody = undefined,
      statsBody = undefined,
      cacheAsFirstPage = false,
      filterCacheKey = getTaskQueueFilterCacheKey(),
      hydrateSubtasks: shouldHydrateSubtasks = false,
    }) => {
      const hasNewSupplemental =
        appointmentsBody !== undefined ||
        readinessAppointmentsBody !== undefined ||
        statsBody !== undefined;
      const effectiveAppointmentsBody =
        appointmentsBody ?? supplementalDataRef.current.appointmentsBody;
      const effectiveReadinessAppointmentsBody =
        readinessAppointmentsBody ?? supplementalDataRef.current.readinessAppointmentsBody;
      const effectiveStatsBody = statsBody ?? supplementalDataRef.current.statsBody;
      const effectiveReadinessWindowKeys =
        readinessAppointmentsBody !== undefined
          ? new Set([INITIAL_READINESS_APPOINTMENT_WINDOW.key])
          : supplementalDataRef.current.readinessWindowKeys || loadedReadinessWindowKeysRef.current;

      if (effectiveAppointmentsBody || effectiveReadinessAppointmentsBody || effectiveStatsBody) {
        supplementalDataRef.current = {
          appointmentsBody: effectiveAppointmentsBody,
          readinessAppointmentsBody: effectiveReadinessAppointmentsBody,
          statsBody: effectiveStatsBody,
          readinessWindowKeys: effectiveReadinessWindowKeys,
          orgScope: hasNewSupplemental
            ? organizationScopeRef.current
            : supplementalDataRef.current.orgScope,
        };
      }

      const shouldPreserveFullTaskQueueMeta =
        !hasFullTaskQueueMeta(nextTaskQueueMeta) &&
        hasFullTaskQueueMeta(taskQueueMetaRef.current) &&
        taskQueueMetaFilterKeyRef.current === filterCacheKey;
      const effectiveTaskQueueMeta = shouldPreserveFullTaskQueueMeta
        ? mergeTaskQueueMeta(taskQueueMetaRef.current, nextTaskQueueMeta)
        : nextTaskQueueMeta;

      storeTaskQueueCache(
        filterCacheKey,
        nextTaskQueueRows,
        effectiveTaskQueueMeta,
        cacheAsFirstPage,
      );

      setTaskQueueRows(nextTaskQueueRows);
      setTaskQueueMeta(effectiveTaskQueueMeta);
      taskQueueMetaRef.current = effectiveTaskQueueMeta;
      if (hasFullTaskQueueMeta(effectiveTaskQueueMeta)) {
        taskQueueMetaFilterKeyRef.current = filterCacheKey;
      }
      lastTaskQueueSyncAtRef.current =
        effectiveTaskQueueMeta?.serverTime || new Date().toISOString();

      syncCareOrchestrationData({
        dispatch,
        taskQueueRows: nextTaskQueueRows,
        taskQueueMeta: hasFullTaskQueueMeta(effectiveTaskQueueMeta)
          ? effectiveTaskQueueMeta
          : careOrchestrationMetaRef.current?.tasks || null,
        appointmentsBody: supplementalDataRef.current.appointmentsBody,
        statsBody: supplementalDataRef.current.statsBody,
      });
      if (supplementalDataRef.current.readinessAppointmentsBody) {
        syncReadinessAppointmentsState(supplementalDataRef.current.readinessAppointmentsBody);
        loadedReadinessWindowKeysRef.current = effectiveReadinessWindowKeys;
        setLoadedReadinessWindowKeys(effectiveReadinessWindowKeys);
      }

      if (shouldHydrateSubtasks) {
        hydrateDelegatedSubtasks({
          taskQueueRows: nextTaskQueueRows,
          readinessAppointmentTasksById: supplementalDataRef.current.readinessAppointmentsBody
            ? normalizeReadinessAppointmentTasksById(
                supplementalDataRef.current.readinessAppointmentsBody,
              )
            : readinessAppointmentTasksByIdRef.current,
        });
      }
    },
    [
      dispatch,
      getTaskQueueFilterCacheKey,
      hydrateDelegatedSubtasks,
      storeTaskQueueCache,
      syncReadinessAppointmentsState,
    ],
  );

  const fetchTaskQueueSnapshot = useCallback(
    async ({
      pagesToLoad = 1,
      sortMode = taskSortModeRef.current,
      team = activeTeamRef.current,
      ownerScope = ownerFilterRef.current,
      statusScope = statusFilterRef.current,
      loadAllPages = false,
      includeSupplemental = false,
    } = {}) => {
      const accumulatedRows = [];
      let latestTaskQueueMeta = null;
      let pageIndex = 0;
      let shouldContinue = true;

      /* eslint-disable no-await-in-loop */
      while (shouldContinue) {
        const { taskQueueRows: nextPageRows, taskQueueMeta: nextPageMeta } =
          await fetchTaskQueueData({
            pageSize: TASK_QUEUE_PAGE_SIZE,
            offset: pageIndex * TASK_QUEUE_PAGE_SIZE,
            sortMode,
            team,
            ownerScope,
            statusScope,
            organizationScope: organizationScopeRef.current,
            departmentAthenaIds: departmentScopeRef.current,
          });

        latestTaskQueueMeta =
          pageIndex === 0 ? nextPageMeta : mergeTaskQueueMeta(latestTaskQueueMeta, nextPageMeta);
        accumulatedRows.push(...nextPageRows);

        const total = nextPageMeta?.total ?? accumulatedRows.length;
        const resolvedPagesToLoad = Math.max(1, pagesToLoad);
        const hasLoadedRequestedPages = !loadAllPages && pageIndex + 1 >= resolvedPagesToLoad;

        if (!nextPageRows.length || accumulatedRows.length >= total || hasLoadedRequestedPages) {
          shouldContinue = false;
          continue;
        }

        pageIndex += 1;
      }
      /* eslint-enable no-await-in-loop */

      let supplementalData = {};
      if (includeSupplemental) {
        supplementalData = await fetchSupplementalDataOnce(
          getReadinessSupplementalParams(INITIAL_READINESS_APPOINTMENT_WINDOW),
        );
      }

      return {
        taskQueueRows: accumulatedRows,
        taskQueueMeta: latestTaskQueueMeta,
        ...supplementalData,
      };
    },
    [fetchSupplementalDataOnce, getReadinessSupplementalParams],
  );

  const refreshCapacityTaskData = useCallback(async () => {
    if (!currentUserId || !organizationScopeRef.current) return [];

    const { taskQueueRows: nextCapacityTasks } = await fetchTaskQueueSnapshot({
      pagesToLoad: 1,
      sortMode: TASK_QUEUE_SORT_MODE.PatientPriority,
      team: 'all',
      ownerScope: 'my',
      statusScope: 'open',
      loadAllPages: true,
    });

    setCapacityTasksState(nextCapacityTasks);
    return nextCapacityTasks;
  }, [currentUserId, fetchTaskQueueSnapshot, setCapacityTasksState]);

  const refreshCapacityTaskDataSafely = useCallback(
    async () => refreshCapacityTaskData().catch(() => null),
    [refreshCapacityTaskData],
  );

  const loadPostVisitReadinessAppointments = useCallback(async () => {
    if (isLoadingPostVisitReadinessRef.current) return;
    isLoadingPostVisitReadinessRef.current = true;
    try {
      const body = await fetchReadinessAppointmentsData({
        organizationScope: organizationScopeRef.current,
        workflowScope: WORKFLOW_SCOPE.POST_VISIT,
      });
      postVisitReadinessBodyRef.current = body;
      syncPostVisitReadinessState(body);
    } catch (error) {
      showAlert(AlertType.Error, '', error?.message || 'Failed to load appointments');
    } finally {
      isLoadingPostVisitReadinessRef.current = false;
    }
  }, [AlertType.Error, showAlert, syncPostVisitReadinessState]);

  const refreshCareOrchestrationData = useCallback(async () => {
    const loadedCount = Math.max(taskQueueRowsRef.current.length, TASK_QUEUE_PAGE_SIZE);
    const pagesToLoad = Math.max(1, Math.ceil(loadedCount / TASK_QUEUE_PAGE_SIZE));
    const {
      taskQueueRows: nextTaskQueueRows,
      taskQueueMeta: nextTaskQueueMeta,
      appointmentsBody,
      readinessAppointmentsBody,
      statsBody,
    } = await fetchTaskQueueSnapshot({
      pagesToLoad,
      sortMode: taskSortModeRef.current,
      team: activeTeamRef.current,
      ownerScope: ownerFilterRef.current,
      statusScope: statusFilterRef.current,
      includeSupplemental: true,
    });

    syncTaskQueueState({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      appointmentsBody,
      readinessAppointmentsBody,
      statsBody,
      hydrateSubtasks: true,
    });
    refreshTaskQueueMetaData().catch(() => null);

    postVisitReadinessBodyRef.current = null;
    if (
      workflowScopeRef.current === WORKFLOW_SCOPE.POST_VISIT ||
      workflowScopeRef.current === WORKFLOW_SCOPE.SPLIT
    ) {
      loadPostVisitReadinessAppointments();
    }
  }, [
    fetchTaskQueueSnapshot,
    loadPostVisitReadinessAppointments,
    refreshTaskQueueMetaData,
    syncTaskQueueState,
  ]);

  const refreshTaskQueueData = useCallback(async () => {
    const loadedCount = Math.max(taskQueueRowsRef.current.length, TASK_QUEUE_PAGE_SIZE);
    const pagesToLoad = Math.max(1, Math.ceil(loadedCount / TASK_QUEUE_PAGE_SIZE));
    const { taskQueueRows: nextTaskQueueRows, taskQueueMeta: nextTaskQueueMeta } =
      await fetchTaskQueueSnapshot({
        pagesToLoad,
        sortMode: taskSortModeRef.current,
        team: activeTeamRef.current,
        ownerScope: ownerFilterRef.current,
        statusScope: statusFilterRef.current,
      });

    syncTaskQueueState({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      hydrateSubtasks: true,
    });
    refreshTaskQueueMetaData().catch(() => null);
  }, [fetchTaskQueueSnapshot, refreshTaskQueueMetaData, syncTaskQueueState]);

  const loadMoreTaskQueueData = useCallback(async () => {
    if (isLoadingMoreTasks) return false;

    const loadedRows = taskQueueRowsRef.current;
    const currentMeta = taskQueueMetaRef.current;
    const total = currentMeta?.total ?? loadedRows.length;
    if (loadedRows.length >= total) return false;

    setIsLoadingMoreTasks(true);

    try {
      const { taskQueueRows: nextPageRows, taskQueueMeta: nextPageMeta } = await fetchTaskQueueData(
        {
          pageSize: TASK_QUEUE_PAGE_SIZE,
          offset: loadedRows.length,
          sortMode: taskSortModeRef.current,
          team: activeTeamRef.current,
          ownerScope: ownerFilterRef.current,
          statusScope: statusFilterRef.current,
          organizationScope: organizationScopeRef.current,
          departmentAthenaIds: departmentScopeRef.current,
        },
      );

      syncTaskQueueState({
        nextTaskQueueRows: [...loadedRows, ...nextPageRows],
        nextTaskQueueMeta: mergeTaskQueueMeta(currentMeta, nextPageMeta),
        hydrateSubtasks: true,
      });

      return nextPageRows.length > 0;
    } finally {
      setIsLoadingMoreTasks(false);
    }
  }, [isLoadingMoreTasks, syncTaskQueueState]);

  const loadNextReadinessAppointmentsWindow = useCallback(async () => {
    if (isLoadingMoreReadinessAppointmentsRef.current) return false;

    const nextWindow = READINESS_APPOINTMENT_WINDOWS.find(
      (readinessWindow) => !loadedReadinessWindowKeysRef.current.has(readinessWindow.key),
    );
    if (!nextWindow) return false;

    isLoadingMoreReadinessAppointmentsRef.current = true;
    const requestedWindowKeys = new Set(loadedReadinessWindowKeysRef.current);
    requestedWindowKeys.add(nextWindow.key);
    loadedReadinessWindowKeysRef.current = requestedWindowKeys;
    setLoadedReadinessWindowKeys(requestedWindowKeys);

    try {
      const readinessAppointmentsBody = await fetchReadinessAppointmentsData(
        getReadinessSupplementalParams(nextWindow),
      );

      syncReadinessAppointmentsState(readinessAppointmentsBody, { append: true });

      const nextLoadedWindowKeys = new Set(requestedWindowKeys);
      loadedReadinessWindowKeysRef.current = nextLoadedWindowKeys;
      setLoadedReadinessWindowKeys(nextLoadedWindowKeys);
      supplementalDataRef.current = {
        ...supplementalDataRef.current,
        readinessAppointmentsBody: mergeReadinessAppointmentsBody(
          supplementalDataRef.current.readinessAppointmentsBody,
          readinessAppointmentsBody,
        ),
        readinessWindowKeys: nextLoadedWindowKeys,
      };

      return (readinessAppointmentsBody?.data || []).length > 0;
    } catch (error) {
      showAlert(AlertType.Error, '', error?.message || 'Failed to load more appointments');
      return false;
    } finally {
      isLoadingMoreReadinessAppointmentsRef.current = false;
    }
  }, [AlertType.Error, getReadinessSupplementalParams, showAlert, syncReadinessAppointmentsState]);

  const hasMoreTasks = (taskQueueMeta?.total ?? taskQueueRows.length) > taskQueueRows.length;

  const expandTaskSearchScope = useCallback(async () => {
    const currentMeta = taskQueueMetaRef.current;
    const currentRows = taskQueueRowsRef.current;
    const total = currentMeta?.total ?? currentRows.length;

    if (!total || currentRows.length >= total || isExpandingTaskSearch) {
      return currentRows;
    }

    setIsExpandingTaskSearch(true);

    try {
      const pagesToLoad = Math.max(1, Math.ceil(total / TASK_QUEUE_PAGE_SIZE));
      const { taskQueueRows: nextTaskQueueRows, taskQueueMeta: nextTaskQueueMeta } =
        await fetchTaskQueueSnapshot({
          pagesToLoad,
          sortMode: taskSortModeRef.current,
          team: activeTeamRef.current,
          ownerScope: ownerFilterRef.current,
          statusScope: statusFilterRef.current,
        });

      syncTaskQueueState({
        nextTaskQueueRows,
        nextTaskQueueMeta,
        hydrateSubtasks: true,
      });
      refreshTaskQueueMetaData().catch(() => null);

      return nextTaskQueueRows;
    } finally {
      setIsExpandingTaskSearch(false);
    }
  }, [fetchTaskQueueSnapshot, isExpandingTaskSearch, refreshTaskQueueMetaData, syncTaskQueueState]);

  const resetTaskSearchScope = useCallback(async () => {
    const cacheKey = getTaskQueueFilterCacheKey();
    const cachedFirstPage = firstPageTaskQueueByFilterKeyRef.current[cacheKey];

    if (cachedFirstPage) {
      syncTaskQueueState({
        nextTaskQueueRows: cachedFirstPage.taskQueueRows,
        nextTaskQueueMeta: cachedFirstPage.taskQueueMeta,
        filterCacheKey: cacheKey,
        hydrateSubtasks: true,
      });
      refreshTaskQueueMetaData({ filterCacheKey: cacheKey }).catch(() => null);
      return;
    }

    const { taskQueueRows: nextTaskQueueRows, taskQueueMeta: nextTaskQueueMeta } =
      await fetchTaskQueueSnapshot({
        pagesToLoad: 1,
        sortMode: taskSortModeRef.current,
        team: activeTeamRef.current,
        ownerScope: ownerFilterRef.current,
        statusScope: statusFilterRef.current,
        loadAllPages: shouldHydrateEntireTaskQueue(taskSortModeRef.current),
      });

    syncTaskQueueState({
      nextTaskQueueRows,
      nextTaskQueueMeta,
      cacheAsFirstPage: true,
      filterCacheKey: cacheKey,
      hydrateSubtasks: true,
    });
    refreshTaskQueueMetaData({ filterCacheKey: cacheKey }).catch(() => null);
  }, [
    fetchTaskQueueSnapshot,
    getTaskQueueFilterCacheKey,
    refreshTaskQueueMetaData,
    shouldHydrateEntireTaskQueue,
    syncTaskQueueState,
  ]);

  const refreshSupplementalData = useCallback(async () => {
    if (isRefreshingSupplementalDataRef.current) {
      shouldRefreshSupplementalDataAgainRef.current = true;
      return;
    }

    isRefreshingSupplementalDataRef.current = true;

    try {
      const { appointmentsBody } = await fetchSupplementalDataOnce(
        getReadinessSupplementalParams(INITIAL_READINESS_APPOINTMENT_WINDOW),
      );
      const { readinessAppointmentsBody, loadedWindowKeys: nextLoadedWindowKeys } =
        await fetchReadinessAppointmentsForLoadedWindows();
      const existingStatsBody = supplementalDataRef.current.statsBody;

      syncDashboardV2Appointments(appointmentsBody);
      syncReadinessAppointmentsState(readinessAppointmentsBody);
      loadedReadinessWindowKeysRef.current = nextLoadedWindowKeys;
      setLoadedReadinessWindowKeys(nextLoadedWindowKeys);
      const currentMeta = careOrchestrationMetaRef.current;
      dispatch(
        setCareOrchestrationMeta({
          tasks: currentMeta?.tasks || null,
          appointments: appointmentsBody?.meta || null,
          stats: existingStatsBody?.meta || currentMeta?.stats || null,
          readiness: currentMeta?.readiness || null,
        }),
      );
      supplementalDataRef.current = {
        appointmentsBody,
        readinessAppointmentsBody,
        statsBody: existingStatsBody,
        orgScope: organizationScopeRef.current,
        readinessWindowKeys: nextLoadedWindowKeys,
      };
      dispatch(setCareOrchestrationLastFetchedAt(new Date().toISOString()));
      fetchStatsDataOnce()
        .then((statsBody) => {
          supplementalDataRef.current = {
            ...supplementalDataRef.current,
            statsBody,
          };
          dispatch(setCareOrchestrationStats(statsBody?.data || null));
          const latestMeta = careOrchestrationMetaRef.current;
          dispatch(
            setCareOrchestrationMeta({
              tasks: latestMeta?.tasks || null,
              appointments: supplementalDataRef.current.appointmentsBody?.meta || null,
              stats: statsBody?.meta || null,
              readiness: latestMeta?.readiness || null,
            }),
          );
        })
        .catch(() => null);
    } finally {
      isRefreshingSupplementalDataRef.current = false;

      if (shouldRefreshSupplementalDataAgainRef.current) {
        shouldRefreshSupplementalDataAgainRef.current = false;
        refreshSupplementalData();
      }
    }
  }, [
    dispatch,
    fetchReadinessAppointmentsForLoadedWindows,
    fetchStatsDataOnce,
    fetchSupplementalDataOnce,
    getReadinessSupplementalParams,
    syncReadinessAppointmentsState,
  ]);

  useEffect(() => {
    if (!organizationScope) return;

    let cancelled = false;

    const run = async () => {
      const cacheKey = getTaskQueueFilterCacheKey({
        sortMode: taskSortMode,
        team: activeTeam,
        ownerScope: ownerFilter,
        statusScope: statusFilter,
        orgScope: organizationScope,
        deptScope: activeScopeDepartmentAthenaIds,
      });
      const cachedTaskQueue = taskQueueCacheRef.current[cacheKey];

      if (taskQueueRequestKeyRef.current === cacheKey) {
        return;
      }

      dispatch(setCareOrchestrationLoading(true));
      dispatch(setCareOrchestrationError(null));
      setReadinessSourceAppointments([]);
      loadedReadinessWindowKeysRef.current = new Set();
      setLoadedReadinessWindowKeys(new Set());

      try {
        if (cachedTaskQueue) {
          if (cancelled) return;

          syncTaskQueueState({
            nextTaskQueueRows: cachedTaskQueue.taskQueueRows,
            nextTaskQueueMeta: cachedTaskQueue.taskQueueMeta,
            filterCacheKey: cacheKey,
            hydrateSubtasks: true,
          });
          refreshTaskQueueMetaData({
            sortMode: taskSortMode,
            team: activeTeam,
            ownerScope: ownerFilter,
            statusScope: statusFilter,
            orgScope: organizationScope,
            deptScope: activeScopeDepartmentAthenaIds,
            filterCacheKey: cacheKey,
          }).catch(() => null);

          const needsSupplemental =
            view === TAB_KEYS.READINESS &&
            (!supplementalDataRef.current.appointmentsBody ||
              !supplementalDataRef.current.readinessAppointmentsBody ||
              supplementalDataRef.current.orgScope !== organizationScopeRef.current);

          if (needsSupplemental && !cancelled) {
            await refreshSupplementalData();
          }

          return;
        }

        taskQueueRequestKeyRef.current = cacheKey;
        const shouldIncludeSupplemental = view === TAB_KEYS.READINESS;

        const {
          taskQueueRows: nextTaskQueueRows,
          taskQueueMeta: nextTaskQueueMeta,
          appointmentsBody,
          readinessAppointmentsBody,
          statsBody,
        } = await fetchTaskQueueSnapshot({
          pagesToLoad: 1,
          sortMode: taskSortMode,
          team: activeTeam,
          ownerScope: ownerFilter,
          statusScope: statusFilter,
          loadAllPages: shouldHydrateEntireTaskQueue(taskSortMode),
          includeSupplemental: shouldIncludeSupplemental,
        });

        if (cancelled) return;

        const latestCacheKey = getTaskQueueFilterCacheKey({
          sortMode: taskSortModeRef.current,
          team: activeTeamRef.current,
          ownerScope: ownerFilterRef.current,
          statusScope: statusFilterRef.current,
          orgScope: organizationScopeRef.current,
          deptScope: departmentScopeRef.current,
        });

        if (latestCacheKey !== cacheKey) {
          storeTaskQueueCache(cacheKey, nextTaskQueueRows, nextTaskQueueMeta, true);
          return;
        }

        syncTaskQueueState({
          nextTaskQueueRows,
          nextTaskQueueMeta,
          appointmentsBody,
          readinessAppointmentsBody,
          statsBody,
          cacheAsFirstPage: true,
          filterCacheKey: cacheKey,
          hydrateSubtasks: true,
        });
        refreshTaskQueueMetaData({
          sortMode: taskSortMode,
          team: activeTeam,
          ownerScope: ownerFilter,
          statusScope: statusFilter,
          orgScope: organizationScope,
          deptScope: activeScopeDepartmentAthenaIds,
          filterCacheKey: cacheKey,
        }).catch(() => null);
      } catch (error) {
        if (cancelled) return;

        const errorMessage = error?.message || 'Failed to load care orchestration data';
        dispatch(setCareOrchestrationError(errorMessage));
        showAlert(AlertType.Error, '', errorMessage);
      } finally {
        if (taskQueueRequestKeyRef.current === cacheKey) {
          taskQueueRequestKeyRef.current = null;
        }
        if (!cancelled) {
          dispatch(setCareOrchestrationLoading(false));
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    activeScopeDepartmentAthenaIds.join('|'),
    activeTeam,
    dispatch,
    fetchTaskQueueSnapshot,
    getTaskQueueFilterCacheKey,
    organizationScope,
    ownerFilter,
    refreshTaskQueueMetaData,
    statusFilter,
    shouldHydrateEntireTaskQueue,
    storeTaskQueueCache,
    syncTaskQueueState,
    taskSortMode,
    view,
  ]);

  const syncLocalTaskRows = useCallback(
    (updater) => {
      tasks.forEach((taskRow) => {
        let nextTaskRow = taskRow;

        if (typeof updater === 'function') {
          nextTaskRow = updater(taskRow);
        } else if (updater?.id && taskRow?.id === updater.id) {
          nextTaskRow = { ...taskRow, ...updater };
        }

        if (nextTaskRow && nextTaskRow !== taskRow) {
          upsertDashboardV2Task(nextTaskRow);
        }
      });

      syncLocalReadinessTaskRows((taskRow) => {
        if (typeof updater === 'function') {
          return updater(taskRow);
        }

        if (updater?.id && taskRow?.id === updater.id) {
          return {
            ...taskRow,
            ...normalizeDashboardV2Task(updater),
          };
        }

        return taskRow;
      });
    },
    [syncLocalReadinessTaskRows, tasks],
  );

  const upsertTaskFromResponse = useCallback(
    (taskRow) => {
      if (!taskRow?.id) return;
      upsertDashboardV2Task(taskRow);
      upsertCapacityTaskFromResponse(taskRow);
      syncLocalReadinessTaskRows(taskRow);
    },
    [syncLocalReadinessTaskRows, upsertCapacityTaskFromResponse],
  );

  useEffect(() => {
    if (view !== TAB_KEYS.READINESS) return;
    if (!organizationScopeRef.current) return;
    if (taskQueueRequestKeyRef.current) return;

    const hasSupplementalData =
      !!supplementalDataRef.current.appointmentsBody &&
      !!supplementalDataRef.current.readinessAppointmentsBody;

    if (hasSupplementalData) {
      if (!supplementalDataRef.current.statsBody) {
        fetchStatsDataOnce()
          .then((statsBody) => {
            supplementalDataRef.current = {
              ...supplementalDataRef.current,
              statsBody,
            };
            dispatch(setCareOrchestrationStats(statsBody?.data || null));
            const currentMeta = careOrchestrationMetaRef.current;
            dispatch(
              setCareOrchestrationMeta({
                tasks: currentMeta?.tasks || null,
                appointments: supplementalDataRef.current.appointmentsBody?.meta || null,
                stats: statsBody?.meta || null,
                readiness: currentMeta?.readiness || null,
              }),
            );
          })
          .catch(() => null);
      }
      return;
    }

    refreshSupplementalData().catch(() => null);
  }, [dispatch, fetchStatsDataOnce, refreshSupplementalData, view]);

  useEffect(() => {
    if (view !== TAB_KEYS.READINESS) return;
    if (!readinessDataReady) return;
    if (loadedReadinessWindowKeys.size === 0) return;
    if (loadedReadinessWindowKeys.size >= READINESS_APPOINTMENT_WINDOWS.length) return;

    loadNextReadinessAppointmentsWindow();
  }, [loadNextReadinessAppointmentsWindow, loadedReadinessWindowKeys, readinessDataReady, view]);

  useEffect(() => {
    if (view !== TAB_KEYS.READINESS) return;
    if (!readinessDataReady) return;
    if (postVisitReadinessBodyRef.current) return;

    loadPostVisitReadinessAppointments();
  }, [loadPostVisitReadinessAppointments, readinessDataReady, view]);

  useEffect(() => {
    if (postVisitOrganizationScopeRef.current === organizationScope) return;
    postVisitOrganizationScopeRef.current = organizationScope;

    postVisitReadinessBodyRef.current = null;
    setPostVisitDataReady(false);
    setPostVisitAppointments([]);

    if (
      workflowScopeRef.current === WORKFLOW_SCOPE.POST_VISIT ||
      workflowScopeRef.current === WORKFLOW_SCOPE.SPLIT
    ) {
      loadPostVisitReadinessAppointments();
    }
  }, [organizationScope, loadPostVisitReadinessAppointments]);

  useEffect(() => {
    if (!organizationScope || !currentUserId) return;
    refreshCapacityTaskDataSafely();
  }, [currentUserId, organizationScope, refreshCapacityTaskDataSafely]);

  const applyTaskQueueChanges = useCallback(
    (changesBody, { invalidateCaches = false } = {}) => {
      const changedTaskRows = changesBody?.data || [];
      const changedSubtasksByParentId = changesBody?.changedSubtasksByParentId || {};
      const removedTaskIds = changesBody?.removedTaskIds || [];
      const hasChangedRows = changedTaskRows.length > 0;
      const hasRemovedRows = removedTaskIds.length > 0;
      const hasChangedSubtasks = Object.keys(changedSubtasksByParentId).length > 0;
      const hasChanges = hasChangedRows || hasRemovedRows || hasChangedSubtasks;

      changedTaskRows.forEach((taskRow) => {
        if (taskRow?.id) {
          upsertDashboardV2Task(taskRow);
          upsertCapacityTaskFromResponse(taskRow);
          syncLocalReadinessTaskRows(taskRow);
        }
      });

      removedTaskIds.forEach((taskId) => {
        if (taskId) {
          deleteOrmItem('DashboardV2Task', taskId);
        }
      });
      removeCapacityTasksById(removedTaskIds);
      removeLocalReadinessTaskRows(removedTaskIds);

      const knownAppointmentIds = new Set(
        appointments.map((appointment) => `${appointment?.id || ''}`.trim()).filter(Boolean),
      );
      const knownReadinessAppointmentIds = new Set(
        readinessSourceAppointments
          .map((appointment) =>
            `${appointment?.sourceAppointmentId || appointment?.id || ''}`.trim(),
          )
          .filter(Boolean),
      );
      const hasTaskForNewAppointment = changedTaskRows.some((taskRow) => {
        const appointmentId = `${taskRow?.externalObjectId || ''}`.trim();
        return appointmentId && !knownAppointmentIds.has(appointmentId);
      });
      const hasTaskForTrackedReadinessAppointment =
        removedTaskIds.some((taskId) => !!appointmentTaskLookupById[taskId]) ||
        changedTaskRows.some((taskRow) => {
          const appointmentId = `${taskRow?.externalObjectId || ''}`.trim();
          return appointmentId && knownReadinessAppointmentIds.has(appointmentId);
        });

      const shouldRefreshReadinessSupplementalData =
        hasTaskForNewAppointment || hasTaskForTrackedReadinessAppointment;

      if (shouldRefreshReadinessSupplementalData) {
        refreshSupplementalData().catch(() => null);
      }

      if (hasChangedSubtasks) {
        syncDelegatedSubtasksState(
          (prev) => ({
            ...prev,
            ...changedSubtasksByParentId,
          }),
          Object.keys(changedSubtasksByParentId),
        );
      }

      const nextTaskQueueMeta = changesBody?.meta || null;
      if (nextTaskQueueMeta) {
        const shouldInvalidateCaches =
          invalidateCaches ||
          hasChanges ||
          hasTaskQueueMetaChanged(taskQueueMetaRef.current, nextTaskQueueMeta);
        syncTaskQueueMetaState(nextTaskQueueMeta, { invalidateCaches: shouldInvalidateCaches });
      }

      if (changesBody?.serverTime) {
        lastTaskQueueSyncAtRef.current = changesBody.serverTime;
      }
    },
    [
      appointmentTaskLookupById,
      appointments,
      readinessSourceAppointments,
      refreshSupplementalData,
      removeLocalReadinessTaskRows,
      removeCapacityTasksById,
      syncTaskQueueMetaState,
      syncLocalReadinessTaskRows,
      upsertCapacityTaskFromResponse,
    ],
  );

  const refreshCareOrchestrationDataSafely = useCallback(
    async () => refreshCareOrchestrationData().catch(() => null),
    [],
  );

  const refreshTaskQueueChanges = useCallback(
    async ({ invalidateCaches = false } = {}) => {
      const since = lastTaskQueueSyncAtRef.current;
      if (!since) {
        await refreshTaskQueueData();
        return false;
      }

      const changesBody = await fetchTaskQueueChanges(since, {
        team: activeTeamRef.current,
        ownerScope: ownerFilterRef.current,
        statusScope: statusFilterRef.current,
        mrn: patientSearchRef.current?.mrn || null,
        organizationScope: organizationScopeRef.current,
        departmentAthenaIds: departmentScopeRef.current,
      });

      applyTaskQueueChanges(changesBody, { invalidateCaches });
      return true;
    },
    [applyTaskQueueChanges, refreshTaskQueueData],
  );

  const refreshTaskQueueDataSafely = useCallback(
    async () => refreshTaskQueueData().catch(() => null),
    [],
  );

  const refreshTaskQueueChangesSafely = useCallback(
    async ({ invalidateCaches = false } = {}) => {
      try {
        await refreshTaskQueueChanges({ invalidateCaches });
      } catch {
        await refreshTaskQueueDataSafely();
      }
    },
    [refreshTaskQueueChanges, refreshTaskQueueDataSafely],
  );

  useEffect(() => {
    if (!hasEnabledTabs) return undefined;

    const pollTaskQueueChanges = async () => {
      try {
        await refreshTaskQueueChanges();
        await refreshCapacityTaskData();
      } catch {
        // Ignore polling failures; the next interval or full refresh will reconcile state.
      }
    };

    const intervalId = window.setInterval(
      pollTaskQueueChanges,
      TASK_QUEUE_CHANGES_POLL_INTERVAL_MS,
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pollTaskQueueChanges();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasEnabledTabs, refreshCapacityTaskData, refreshTaskQueueChanges]);

  const handleAssignToMe = async (taskId) => {
    if (!taskId || assigningTaskIds.has(taskId)) return;

    setAssigningTaskIds((prev) => new Set(prev).add(taskId));

    const task = tasksById[taskId] ?? allTasksById[taskId] ?? appointmentTaskLookupById[taskId];
    const wasFollowingTask = isTaskFollowedByCurrentUser(task, currentUserId);

    try {
      const updatedTask = await assignTaskToMe(taskId);
      let autoUnfollowWarning = null;

      if (wasFollowingTask) {
        try {
          await unfollowTask(taskId);
        } catch (error) {
          const errorMessage = error?.message || 'Failed to unfollow task';

          if (!isTaskFollowStateAlreadyCurrentError(errorMessage)) {
            autoUnfollowWarning = errorMessage;
          }
        }
      }

      upsertTaskFromResponse({
        ...(updatedTask || task),
        isFollowing: false,
        followedBy: (updatedTask?.followedBy || task?.followedBy || []).filter(
          (followerId) => `${followerId ?? ''}`.trim() !== `${currentUserId ?? ''}`.trim(),
        ),
      });
      await refreshTaskQueueChangesSafely();

      if (autoUnfollowWarning) {
        showAlert(
          AlertType.Warning,
          '',
          `Task claimed, but automatic unfollow failed: ${autoUnfollowWarning}`,
        );
      }

      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to assign task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setAssigningTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  const handleMarkComplete = async (taskId, completionPayload = null) => {
    if (!taskId || markingTaskId) return;

    setMarkingTaskId(taskId);
    const taskToComplete =
      tasksById[taskId] ?? allTasksById[taskId] ?? appointmentTaskLookupById[taskId] ?? null;
    const parentTaskId = `${taskToComplete?.parentTaskId || ''}`.trim();
    const delegatedSubtasksParentId = parentTaskId || `${taskId}`.trim();

    try {
      const updatedTask = await markTaskComplete(taskId, completionPayload);
      upsertTaskFromResponse(updatedTask);
      setSessionCompletedTaskIds((prev) => ({ ...prev, [taskId]: true }));

      const refreshedSubtasks = delegatedSubtasksParentId
        ? await fetchTaskSubtasks(delegatedSubtasksParentId)
        : [];

      if (Array.isArray(refreshedSubtasks)) {
        refreshedSubtasks.forEach((subtask) => {
          upsertTaskFromResponse(subtask);
        });

        if (delegatedSubtasksParentId) {
          syncDelegatedSubtasksState(
            (prev) => ({
              ...prev,
              [delegatedSubtasksParentId]: refreshedSubtasks,
            }),
            [delegatedSubtasksParentId],
          );
        }
      }

      await refreshTaskQueueChangesSafely();
      showAlert(AlertType.Success, '', 'Task successfully marked as completed.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to mark task complete';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setMarkingTaskId(null);
    }
  };

  const handleCancelTask = async (taskId, reason, cancelPayload = null) => {
    const normalizedReason = `${reason || ''}`.trim();
    if (!taskId || cancelingTaskId || !normalizedReason) return false;

    setCancelingTaskId(taskId);
    const taskToCancel =
      tasksById[taskId] ?? allTasksById[taskId] ?? appointmentTaskLookupById[taskId] ?? null;
    const parentTaskId = `${taskToCancel?.parentTaskId || ''}`.trim();
    const delegatedSubtasksParentId = parentTaskId || `${taskId}`.trim();

    try {
      const updatedTask = await cancelTask(taskId, normalizedReason, cancelPayload);
      upsertTaskFromResponse(updatedTask);
      setSessionCompletedTaskIds((prev) => ({ ...prev, [taskId]: true }));

      const refreshedSubtasks = delegatedSubtasksParentId
        ? await fetchTaskSubtasks(delegatedSubtasksParentId)
        : [];

      if (Array.isArray(refreshedSubtasks)) {
        refreshedSubtasks.forEach((subtask) => {
          upsertTaskFromResponse(subtask);
        });

        if (delegatedSubtasksParentId) {
          syncDelegatedSubtasksState(
            (prev) => ({
              ...prev,
              [delegatedSubtasksParentId]: refreshedSubtasks,
            }),
            [delegatedSubtasksParentId],
          );
        }
      }

      await refreshTaskQueueChangesSafely();
      showAlert(AlertType.Success, '', 'Task successfully canceled.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to cancel task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setCancelingTaskId(null);
    }
  };

  const handleUnassignTask = async (taskId) => {
    if (!taskId || unassigningTaskId) return false;

    setUnassigningTaskId(taskId);

    try {
      const updatedTask = await unassignTask(taskId);
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unassign task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnassigningTaskId(null);
    }
  };

  const handleFollowTask = async (taskId) => {
    if (!taskId || followingTaskId) return false;

    setFollowingTaskId(taskId);

    try {
      await followTask(taskId);
      showAlert(AlertType.Success, '', 'Successfully following task.');
      syncLocalTaskRows((taskRow) => {
        if (taskRow?.id !== taskId) return taskRow;

        const nextFollowedBy = Array.from(
          new Set([...(taskRow?.followedBy || []), currentUserId].filter(Boolean)),
        );

        return {
          ...taskRow,
          isFollowing: true,
          followedBy: nextFollowedBy,
        };
      });
      await refreshTaskQueueChangesSafely();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to follow task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setFollowingTaskId(null);
    }
  };

  const handleUnfollowTask = async (taskId) => {
    if (!taskId || unfollowingTaskId) return false;

    setUnfollowingTaskId(taskId);

    try {
      await unfollowTask(taskId);
      showAlert(AlertType.Success, '', 'Successfully unfollowed task.');
      syncLocalTaskRows((taskRow) => {
        if (taskRow?.id !== taskId) return taskRow;

        return {
          ...taskRow,
          isFollowing: false,
          followedBy: (taskRow?.followedBy || []).filter(
            (followerId) => `${followerId ?? ''}`.trim() !== `${currentUserId ?? ''}`.trim(),
          ),
        };
      });
      await refreshTaskQueueChangesSafely();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unfollow task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnfollowingTaskId(null);
    }
  };

  const handleReassignTask = async (taskId, assigneeId) => {
    const normalizedAssigneeId = Number(assigneeId);
    if (!taskId || !Number.isInteger(normalizedAssigneeId) || reassigningTaskId) return false;

    setReassigningTaskId(taskId);

    try {
      const updatedTask = await reassignTask(taskId, normalizedAssigneeId);
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to reassign task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setReassigningTaskId(null);
    }
  };

  const handleBulkApply = async (proposals) => {
    if (!proposals?.length) return;
    try {
      const assignments = proposals.map((p) => ({
        taskId: p.taskId,
        assigneeId: Number(p.toClientId),
      }));
      const response = await bulkReassignTasks(assignments);
      const updatedTasks = Array.isArray(response)
        ? response
        : (response?.data ?? response?.tasks ?? []);
      updatedTasks.forEach((task) => upsertTaskFromResponse(task));
      setSelectedTaskIds(new Set());

      const n = proposals.length;
      const distinctAssignees = new Set(proposals.map((p) => p.toClientId)).size;
      const reverseAssignments = proposals.map((p) => ({
        taskId: p.taskId,
        assigneeId: p.fromClientId != null ? Number(p.fromClientId) : null,
      }));

      const handleUndo = async () => {
        try {
          const undoResponse = await bulkReassignTasks(reverseAssignments);
          const undoTasks = Array.isArray(undoResponse)
            ? undoResponse
            : (undoResponse?.data ?? undoResponse?.tasks ?? []);
          undoTasks.forEach((task) => upsertTaskFromResponse(task));
        } catch (undoError) {
          showAlert(AlertType.Error, '', undoError?.message || 'Failed to undo assignments.');
        }
      };

      setBulkAssignToast({
        message: `${n} ${n === 1 ? 'task' : 'tasks'} assigned across ${distinctAssignees} ${distinctAssignees === 1 ? 'person' : 'people'}`,
        action: { label: 'Undo', onClick: handleUndo },
      });
    } catch (error) {
      showAlert(AlertType.Error, '', error?.message || 'Failed to assign tasks.');
    }
  };

  const handleBlockTask = async (taskId, blockedReason) => {
    if (!taskId || blockedReason == null || blockingTaskId) return false;

    setBlockingTaskId(taskId);

    try {
      const updatedTask = await blockTask(taskId, blockedReason);
      showAlert(AlertType.Success, '', 'Task successfully blocked.');
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to block task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setBlockingTaskId(null);
    }
  };

  const handleUnblockTask = async (taskId) => {
    if (!taskId || unblockingTaskId) return false;

    setUnblockingTaskId(taskId);

    try {
      const updatedTask = await unblockTask(taskId);
      showAlert(AlertType.Success, '', 'Task successfully unblocked.');
      upsertTaskFromResponse(updatedTask);
      await refreshTaskQueueChangesSafely();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unblock task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnblockingTaskId(null);
    }
  };

  const handleEscalateTask = async (taskId, escalationPayload = {}) => {
    const normalizedPayload = normalizeEscalationPayload(escalationPayload);

    if (!taskId || escalatingTaskId || !isEscalationPayloadValid(normalizedPayload)) {
      return false;
    }

    setEscalatingTaskId(taskId);

    try {
      await escalateTask(taskId, normalizedPayload);

      let lastUpdatedTask;
      if (Number.isInteger(normalizedPayload.escalatedToUser)) {
        lastUpdatedTask = await reassignTask(taskId, normalizedPayload.escalatedToUser);
      } else {
        lastUpdatedTask = await unassignTask(taskId);
      }

      upsertTaskFromResponse(lastUpdatedTask);
      await refreshTaskQueueChangesSafely();
      showAlert(AlertType.Success, '', 'Task successfully escalated.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to escalate task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setEscalatingTaskId(null);
    }
  };

  const handleDeescalateTask = async (taskId) => {
    if (!taskId || deescalatingTaskId) return false;

    setDeescalatingTaskId(taskId);

    try {
      const updatedTask = await deescalateTask(taskId);

      const previousAssigneeIdValue =
        tasksById[taskId]?.previousAssigneeId ?? updatedTask?.previousAssigneeId;
      const previousAssigneeId =
        previousAssigneeIdValue == null ? null : Number(previousAssigneeIdValue);
      if (Number.isInteger(previousAssigneeId) && previousAssigneeId > 0) {
        const reassignedTask = await reassignTask(taskId, previousAssigneeId);
        upsertTaskFromResponse(reassignedTask);
      } else {
        const unassignedTask = await unassignTask(taskId);
        upsertTaskFromResponse(unassignedTask);
      }

      await refreshTaskQueueChangesSafely();
      showAlert(AlertType.Success, '', 'Task successfully de-escalated.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to de-escalate task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setDeescalatingTaskId(null);
    }
  };

  const handleDelegateSubtasks = async (task, subtasks = []) => {
    const taskId = task?.id;
    const patientId = Number(getTaskPatientId(task) ?? task?.patientId ?? task?.patient?.id);
    const typeId = Number(task?.typeId);
    const reasonId = Number(task?.reasonId);

    if (
      !taskId ||
      delegatingSubtasksTaskId ||
      !Number.isInteger(patientId) ||
      !Number.isInteger(typeId) ||
      !Number.isInteger(reasonId) ||
      !Array.isArray(subtasks) ||
      !subtasks.length
    ) {
      return false;
    }

    const tasksPayload = subtasks
      .map((subtask) => {
        const subject = `${subtask?.title || ''}`.trim();
        const description = `${subtask?.note || ''}`.trim();
        const team = `${subtask?.team || ''}`.trim().toLowerCase();
        const assigneeId = Number(subtask?.assignee?.userId);
        const hasDirectAssignee =
          subtask?.assignee?.type === 'user' && Number.isInteger(assigneeId);
        const priority = `${subtask?.priority || ''}`.trim().toLowerCase();
        const due = `${subtask?.due || ''}`.trim();

        if (!subject || (!team && !hasDirectAssignee)) return null;

        return {
          patientId,
          subject,
          typeId,
          reasonId,
          description,
          priority,
          taskDueAt: due ? new Date(`${due}T00:00:00Z`).toISOString() : null,
          parentTaskId: taskId,
          ...(hasDirectAssignee ? { assigneeId } : { team }),
        };
      })
      .filter(Boolean);

    if (!tasksPayload.length || tasksPayload.length !== subtasks.length) {
      return false;
    }

    setDelegatingSubtasksTaskId(taskId);

    try {
      await delegateSubtasks({ tasks: tasksPayload });
      await refreshTaskQueueDataSafely();
      showAlert(AlertType.Success, '', 'Sub-tasks successfully created.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to create sub-tasks';
      await refreshTaskQueueDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setDelegatingSubtasksTaskId(null);
    }
  };

  const handleCancelSubtask = async (taskId) => {
    if (!taskId || cancelingSubtaskId) return false;

    setCancelingSubtaskId(taskId);
    const subtask =
      tasksById[taskId] ?? allTasksById[taskId] ?? appointmentTaskLookupById[taskId] ?? null;
    const parentTaskId = `${subtask?.parentTaskId || ''}`.trim();

    try {
      await dispatch(updatePatientTask(taskId, { status: PatientTaskStatus.Canceled.value }));

      const canceledSubtask = {
        ...(subtask || {}),
        id: taskId,
        status: PatientTaskStatus.Canceled.value,
      };

      upsertTaskFromResponse(canceledSubtask);

      if (parentTaskId) {
        syncDelegatedSubtasksState(
          (prev) => ({
            ...prev,
            [parentTaskId]: (prev[parentTaskId] || []).map((childTask) =>
              `${childTask?.id || ''}`.trim() === `${taskId}`
                ? {
                    ...childTask,
                    status: PatientTaskStatus.Canceled.value,
                  }
                : childTask,
            ),
          }),
          [parentTaskId],
        );
      }

      showAlert(AlertType.Success, '', 'Sub-task successfully canceled.');
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to cancel sub-task';
      await refreshCareOrchestrationDataSafely();
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setCancelingSubtaskId(null);
    }
  };

  useEffect(() => {
    if (selectedTask?.id && !selectedTask?.parentTaskId && !allTasksById[selectedTask.id]) {
      setSelectedTask(null);
      dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
    }
    if (taskFromReadiness?.id && !appointmentTaskLookupById[taskFromReadiness.id]) {
      setTaskFromReadiness(null);
      dispatch(saveCareOrchestrationState({ taskFromReadinessId: null }));
    }
  }, [allTasksById, appointmentTaskLookupById, dispatch, selectedTask, taskFromReadiness]);

  useEffect(() => {
    if (!selectedTask?.id) return;
    const nextSelectedTask = allTasksById[selectedTask.id] ?? tasksById[selectedTask.id];
    if (!nextSelectedTask) return;
    if (nextSelectedTask !== selectedTask) {
      setSelectedTask(nextSelectedTask);
    }
  }, [allTasksById, selectedTask, tasksById]);

  useEffect(() => {
    if (!taskFromReadiness?.id) return;
    const nextTaskFromReadiness = appointmentTaskLookupById[taskFromReadiness.id];
    if (!nextTaskFromReadiness) return;
    if (nextTaskFromReadiness !== taskFromReadiness) {
      setTaskFromReadiness(nextTaskFromReadiness);
    }
  }, [appointmentTaskLookupById, taskFromReadiness]);

  useEffect(() => {
    if (selectedAppt?.id && !appointmentsById[selectedAppt.id]) {
      setSelectedAppt(null);
      dispatch(saveCareOrchestrationState({ selectedApptId: null }));
    }
  }, [appointmentsById, dispatch, selectedAppt]);

  useEffect(() => {
    if (!selectedAppt?.id) return;
    const nextSelectedAppt = appointmentsById[selectedAppt.id];
    if (!nextSelectedAppt) return;
    if (nextSelectedAppt !== selectedAppt) {
      setSelectedAppt(nextSelectedAppt);
    }
  }, [selectedAppt, appointmentsById]);

  useEffect(() => {
    if (selectedTask || !persistedState?.selectedTaskId) return;
    const nextSelectedTask =
      allTasksById[persistedState.selectedTaskId] ?? tasksById[persistedState.selectedTaskId];
    if (nextSelectedTask) {
      setSelectedTask(nextSelectedTask);
    }
  }, [allTasksById, persistedState?.selectedTaskId, selectedTask, tasksById]);

  useEffect(() => {
    if (taskFromReadiness || !persistedState?.taskFromReadinessId) return;
    const nextTaskFromReadiness = appointmentTaskLookupById[persistedState.taskFromReadinessId];
    if (nextTaskFromReadiness) {
      setTaskFromReadiness(nextTaskFromReadiness);
    }
  }, [appointmentTaskLookupById, persistedState?.taskFromReadinessId, taskFromReadiness]);

  useEffect(() => {
    if (selectedAppt || !persistedState?.selectedApptId) return;
    const nextSelectedAppt = appointmentsById[persistedState.selectedApptId];
    if (nextSelectedAppt) {
      setSelectedAppt(nextSelectedAppt);
    }
  }, [appointmentsById, persistedState?.selectedApptId, selectedAppt]);

  useEffect(() => {
    const handleFirstInteraction = () => {
      unlockTaskNotificationSound().catch(() => null);

      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };

    window.addEventListener('pointerdown', handleFirstInteraction);
    window.addEventListener('keydown', handleFirstInteraction);

    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  useEffect(() => {
    setBrowserNotificationPermission(getTaskBrowserNotificationPermission());
  }, []);

  useEffect(() => {
    if (!taskNotificationPreferences?.notificationsEnabled) {
      setTaskNotificationToasts([]);
      return;
    }

    const visibleSentNotifications = getVisibleInAppTaskNotifications(
      taskNotifications,
      taskNotificationPreferences,
    ).filter((notification) => notification?.status === NOTIFICATION_STATUS.Sent);

    const nextToastNotifications = visibleSentNotifications.filter(
      (notification) => !shownTaskNotificationToastIdsRef.current.has(notification.id),
    );

    if (!nextToastNotifications.length) return;

    const timeoutIds = nextToastNotifications.map((notification, index) =>
      window.setTimeout(() => {
        shownTaskNotificationToastIdsRef.current.add(notification.id);
        saveToastedTaskNotificationIds(shownTaskNotificationToastIdsRef.current);

        if (taskNotificationPreferences?.sound) {
          playTaskNotificationSound(notification?.category === NOTIFICATION_CATEGORY.Critical);
        }

        setTaskNotificationToasts((prev) => {
          if (prev.some((toast) => toast.id === notification.id)) {
            return prev;
          }

          return [notification, ...prev].slice(0, MAX_TASK_NOTIFICATION_TOASTS);
        });
      }, index * TASK_NOTIFICATION_TOAST_STAGGER_MS),
    );

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [taskNotifications, taskNotificationPreferences]);

  const clearSelections = useCallback(() => {
    setSelectedTask(null);
    setSelectedAppt(null);
    setTaskFromReadiness(null);
    dispatch(
      saveCareOrchestrationState({
        selectedTaskId: null,
        selectedApptId: null,
        taskFromReadinessId: null,
      }),
    );
  }, [dispatch]);

  const toggleReadinessPinnedTeam = useCallback(
    async (team) => {
      const previousPinnedTeam = readinessPinnedTeam;
      const nextPinnedTeam = previousPinnedTeam === team ? null : team;

      setReadinessPinnedTeam(nextPinnedTeam);

      try {
        const response = await patchMyReadinessPinnedTeam(nextPinnedTeam);
        setReadinessPinnedTeam(response?.pinnedTeam ?? nextPinnedTeam);
      } catch (error) {
        setReadinessPinnedTeam(previousPinnedTeam);
        showAlert(AlertType.Error, '', error?.message || 'Failed to update pinned team');
      }
    },
    [AlertType.Error, readinessPinnedTeam, showAlert],
  );

  const savePinnedDepartmentScope = useCallback(
    async (nextPinnedScope) => {
      const previousPinnedScope = pinnedDepartmentScope;

      setPinnedDepartmentScope(nextPinnedScope);

      try {
        const response = await patchMyPinnedDepartmentScope(nextPinnedScope);
        setPinnedDepartmentScope(response?.pinnedScope ?? nextPinnedScope);
      } catch (error) {
        setPinnedDepartmentScope(previousPinnedScope);
        showAlert(AlertType.Error, '', error?.message || 'Failed to update saved scope');
      }
    },
    [AlertType.Error, pinnedDepartmentScope, showAlert],
  );

  const toggleScopePin = useCallback(() => {
    savePinnedDepartmentScope(
      isPinnedToCurrentScope ? null : scopeToPinnedPayload(scopeFilters, guideEnabled),
    );
  }, [guideEnabled, isPinnedToCurrentScope, savePinnedDepartmentScope, scopeFilters]);
  const resetScopeToPinned = useCallback(() => {
    if (!pinnedDepartmentScope) return;
    setScopeFilters({
      regions: pinnedDepartmentScope.regions,
      counties: pinnedDepartmentScope.counties,
      markets: pinnedDepartmentScope.markets,
      departments: pinnedDepartmentScope.departments,
    });
    setGuideEnabled(Boolean(pinnedDepartmentScope.guideEnabled));
  }, [pinnedDepartmentScope, setScopeFilters, setGuideEnabled]);

  const patchTaskNotificationPreferences = useCallback(
    async (body, optimisticUpdater) => {
      let previousPreferences = null;

      setTaskNotificationPreferences((prev) => {
        previousPreferences = prev;
        return optimisticUpdater ? optimisticUpdater(prev) : prev;
      });
      setIsTaskNotificationPreferencesSaving(true);

      try {
        const response = await patchMyTaskNotificationPreferences(body);
        setTaskNotificationPreferences(response || previousPreferences);
        return response || previousPreferences;
      } catch (error) {
        if (previousPreferences) {
          setTaskNotificationPreferences(previousPreferences);
        }
        showAlert(
          AlertType.Error,
          '',
          error?.message || 'Failed to update notification preferences',
        );
        return null;
      } finally {
        setIsTaskNotificationPreferencesSaving(false);
      }
    },
    [AlertType.Error, showAlert],
  );

  const sendTaskNotificationTestMessage = useCallback(
    async (body) => {
      try {
        await sendMyTaskNotificationTestMessage(body);

        if (body?.emailAddress) {
          showAlert(AlertType.Success, '', 'Test email notification sent.');
        } else if (body?.slackUserId) {
          showAlert(AlertType.Success, '', 'Test Slack notification sent.');
        }

        return true;
      } catch (error) {
        showAlert(
          AlertType.Error,
          '',
          error?.message || 'Failed to send test notification message',
        );
        return false;
      }
    },
    [AlertType.Error, AlertType.Success, showAlert],
  );

  const searchTaskNotificationSlackUsers = useCallback(
    async (query) => searchMyTaskNotificationSlackUsers(query),
    [],
  );

  const handleRequestBrowserNotificationPermission = useCallback(async () => {
    if (!isBrowserNotificationsSupported()) {
      setBrowserNotificationPermission('denied');
      return 'denied';
    }

    const permission = await requestTaskBrowserNotificationPermission();
    setBrowserNotificationPermission(permission);
    return permission;
  }, []);

  const handleCloseTaskNotificationToast = useCallback((toastId) => {
    setTaskNotificationToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  }, []);

  const applyTaskNotificationStatusLocally = useCallback((notificationId, status) => {
    if (!notificationId) return;

    setTaskNotifications((prev) =>
      prev
        .map((notification) =>
          notification?.id === notificationId ? { ...notification, status } : notification,
        )
        .filter(
          (notification) =>
            notification?.status !== NOTIFICATION_STATUS.Dismissed &&
            notification?.status !== NOTIFICATION_STATUS.Pending,
        ),
    );

    if (status !== NOTIFICATION_STATUS.Sent) {
      setTaskNotificationToasts((prev) => prev.filter((toast) => toast.id !== notificationId));
    }
  }, []);

  const handleDismisstaskNotification = useCallback(
    async (notificationId) => {
      if (!notificationId) return false;

      const previousTaskNotifications = taskNotifications;
      const previousTaskNotificationToasts = taskNotificationToasts;

      applyTaskNotificationStatusLocally(notificationId, NOTIFICATION_STATUS.Dismissed);

      try {
        await patchMyTaskNotificationStatus(notificationId, NOTIFICATION_STATUS.Dismissed);
        return true;
      } catch (error) {
        setTaskNotifications(previousTaskNotifications);
        setTaskNotificationToasts(previousTaskNotificationToasts);
        showAlert(AlertType.Error, '', error?.message || 'Failed to dismiss task notification');
        return false;
      }
    },
    [
      AlertType.Error,
      applyTaskNotificationStatusLocally,
      showAlert,
      taskNotificationToasts,
      taskNotifications,
    ],
  );

  const handleOpenTaskNotification = useCallback(
    async (notification) => {
      const taskId = notification?.taskId;
      const patientId = notification?.patientId;
      const shouldOpenWithoutStatusFilter =
        notification?.category === NOTIFICATION_CATEGORY.SubtaskCanceled ||
        notification?.category === NOTIFICATION_CATEGORY.SubtasksCompleted;

      if (!taskId || patientId == null) return false;

      const previousTaskNotifications = taskNotifications;
      const previousTaskNotificationToasts = taskNotificationToasts;

      applyTaskNotificationStatusLocally(notification.id, NOTIFICATION_STATUS.Read);

      try {
        await patchMyTaskNotificationStatus(notification.id, NOTIFICATION_STATUS.Read);

        if (shouldOpenWithoutStatusFilter) {
          const fetchedTask = await fetchTaskQueueItemById(taskId);
          if (fetchedTask?.id) {
            upsertTaskFromResponse(fetchedTask);
          }
        }

        navigate(
          getCareOrchestrationPath(TAB_KEYS.TASKS, {
            statusFilter: shouldOpenWithoutStatusFilter ? null : 'open',
            patientId: `${patientId}`,
            taskId: `${taskId}`,
          }),
        );

        return true;
      } catch (error) {
        setTaskNotifications(previousTaskNotifications);
        setTaskNotificationToasts(previousTaskNotificationToasts);
        showAlert(AlertType.Error, '', error?.message || 'Failed to open task notification');
        return false;
      }
    },
    [
      AlertType.Error,
      NOTIFICATION_CATEGORY.SubtaskCanceled,
      NOTIFICATION_CATEGORY.SubtasksCompleted,
      applyTaskNotificationStatusLocally,
      fetchTaskQueueItemById,
      navigate,
      showAlert,
      taskNotificationToasts,
      taskNotifications,
      upsertTaskFromResponse,
    ],
  );

  const handleMarkAllTaskNotificationsRead = useCallback(async () => {
    const unreadNotificationIds = taskNotifications
      .filter((notification) => notification?.status === NOTIFICATION_STATUS.Sent)
      .map((notification) => notification.id);

    if (!unreadNotificationIds.length) return true;

    const unreadNotificationIdSet = new Set(unreadNotificationIds);
    const previousTaskNotifications = taskNotifications;
    const previousTaskNotificationToasts = taskNotificationToasts;

    setTaskNotifications((prev) =>
      prev.map((notification) =>
        unreadNotificationIdSet.has(notification?.id)
          ? { ...notification, status: NOTIFICATION_STATUS.Read }
          : notification,
      ),
    );
    setTaskNotificationToasts((prev) =>
      prev.filter((toast) => !unreadNotificationIdSet.has(toast.id)),
    );

    try {
      await markAllMyTaskNotificationsRead();
      return true;
    } catch (error) {
      setTaskNotifications(previousTaskNotifications);
      setTaskNotificationToasts(previousTaskNotificationToasts);
      showAlert(
        AlertType.Error,
        '',
        error?.message || 'Failed to mark all task notifications read',
      );
      return false;
    }
  }, [AlertType.Error, showAlert, taskNotificationToasts, taskNotifications]);

  useEffect(() => {
    if (!taskNotificationPreferences?.notificationsEnabled) return;
    if (!isTaskNotificationChannelEnabled(taskNotificationPreferences, 'browser')) return;
    if (browserNotificationPermission !== 'granted') return;

    const visibleSentNotifications = getVisibleBrowserTaskNotifications(
      taskNotifications,
      taskNotificationPreferences,
    ).filter((notification) => notification?.status === NOTIFICATION_STATUS.Sent);

    const nextBrowserNotifications = visibleSentNotifications.filter(
      (notification) => !shownBrowserNotificationIdsRef.current.has(notification.id),
    );

    if (!nextBrowserNotifications.length) return;

    nextBrowserNotifications.forEach((notification) => {
      shownBrowserNotificationIdsRef.current.add(notification.id);

      showTaskBrowserNotification(notification, {
        onClick: async () => {
          await handleOpenTaskNotification(notification);
        },
      });
    });
  }, [
    browserNotificationPermission,
    handleOpenTaskNotification,
    taskNotificationPreferences,
    taskNotifications,
  ]);

  const handleOpenTaskNotificationToast = useCallback(
    async (notification) => {
      setTaskNotificationToasts((prev) => prev.filter((toast) => toast.id !== notification.id));
      await handleOpenTaskNotification(notification);
    },
    [handleOpenTaskNotification],
  );

  const handleSetPatientSearch = useCallback(
    async (nextPatientSearch) => {
      const patientMrn = nextPatientSearch?.mrn ?? nextPatientSearch?.id ?? null;

      if (
        nextPatientSearch?.kind === 'patient' &&
        nextPatientSearch?.id != null &&
        patientMrn != null
      ) {
        setIsExpandingTaskSearch(true);

        try {
          const accumulatedPatientTaskQueueRows = [];
          let patientTaskQueueMeta = null;
          let offset = 0;
          let shouldContinue = true;

          /* eslint-disable no-await-in-loop */
          while (shouldContinue) {
            const {
              taskQueueRows: nextPatientTaskQueueRows,
              taskQueueMeta: nextPatientTaskQueueMeta,
            } = await fetchTaskQueueData({
              pageSize: TASK_QUEUE_PAGE_SIZE,
              offset,
              sortMode: taskSortModeRef.current,
              team: activeTeamRef.current,
              ownerScope: ownerFilterRef.current,
              statusScope: statusFilterRef.current,
              mrn: patientMrn,
              organizationScope: organizationScopeRef.current,
              departmentAthenaIds: departmentScopeRef.current,
            });

            patientTaskQueueMeta =
              offset === 0
                ? nextPatientTaskQueueMeta
                : mergeTaskQueueMeta(patientTaskQueueMeta, nextPatientTaskQueueMeta);
            accumulatedPatientTaskQueueRows.push(...nextPatientTaskQueueRows);

            const total = nextPatientTaskQueueMeta?.total ?? accumulatedPatientTaskQueueRows.length;

            if (
              !nextPatientTaskQueueRows.length ||
              accumulatedPatientTaskQueueRows.length >= total
            ) {
              shouldContinue = false;
              continue;
            }

            offset += TASK_QUEUE_PAGE_SIZE;
          }
          /* eslint-enable no-await-in-loop */

          if (accumulatedPatientTaskQueueRows.length) {
            syncTaskQueueState({
              nextTaskQueueRows: mergeTaskRowsById(
                taskQueueRowsRef.current,
                accumulatedPatientTaskQueueRows,
              ),
              nextTaskQueueMeta: patientTaskQueueMeta ?? taskQueueMetaRef.current,
              hydrateSubtasks: true,
            });
          }
        } catch (error) {
          showAlert(
            AlertType.Error,
            '',
            error?.message || 'Unable to load tasks for the selected patient.',
          );
        } finally {
          setIsExpandingTaskSearch(false);
        }
      }

      setPatientSearch(nextPatientSearch);
      dispatch(saveCareOrchestrationState({ patientSearch: nextPatientSearch }));
    },
    [AlertType.Error, dispatch, showAlert, syncTaskQueueState],
  );

  const handleSearchTaskPatients = useCallback(async (query) => {
    const trimmedQuery = `${query || ''}`.trim();
    if (!trimmedQuery) return [];

    const { rows } = await fetchTaskSearchPatients({
      query: trimmedQuery,
      team: activeTeamRef.current,
      ownerScope: ownerFilterRef.current,
      statusScope: statusFilterRef.current,
    });

    const scope = organizationScopeRef.current;
    const filteredRows = rows.filter((row) => {
      if (!scope) return true;
      const p = row?.patient || {};
      if (scope === OrgScope.Mindspan) return p.cognitiveStatus === PlatformStatus.Active;
      if (scope === OrgScope.Perry) return p.diabetesStatus === PlatformStatus.Active;
      return true;
    });

    return filteredRows.map((row) => {
      const patient = row?.patient || {};
      const taskSummary = row?.taskSummary || {};

      return {
        key: `patient-${patient?.id ?? patient?.mrn ?? ''}`,
        label:
          `${patient?.preferredName || patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
        count: taskSummary?.count ?? 0,
        value: {
          kind: 'patient',
          ...patient,
          taskSummary,
        },
      };
    });
  }, []);

  const handleClearTaskSearch = useCallback(async () => {
    handleSetPatientSearch(null);
    await resetTaskSearchScope();
  }, [handleSetPatientSearch, resetTaskSearchScope]);

  const expandPatientGroup = useCallback(
    (patientGroupKey) => {
      if (!patientGroupKey || expandedPatients[patientGroupKey]) return;

      const nextExpandedPatients = {
        ...expandedPatients,
        [patientGroupKey]: true,
      };

      setExpandedPatients(nextExpandedPatients);
      dispatch(saveCareOrchestrationState({ expandedPatients: nextExpandedPatients }));
    },
    [dispatch, expandedPatients],
  );

  useEffect(() => {
    if (!hasEnabledTabs) {
      if (!featureFlagsLoaded) return;
      if (
        selectedTask ||
        selectedAppt ||
        taskFromReadiness ||
        persistedState?.selectedTaskId ||
        persistedState?.selectedApptId ||
        persistedState?.taskFromReadinessId
      ) {
        clearSelections();
      }
      if (location.pathname !== CARE_ORCHESTRATION_BASE_PATH) {
        navigate(CARE_ORCHESTRATION_BASE_PATH, { replace: true });
      }
      return;
    }

    const hasDeepLink =
      (view === TAB_KEYS.TASKS && (requestedRoute.statusFilter || requestedRoute.taskId)) ||
      (view === TAB_KEYS.LEADS && (requestedRoute.leadTaskId || requestedRoute.leadResourceId));
    const canonicalPath = getCareOrchestrationPath(view, hasDeepLink ? requestedRoute : null);
    if (location.pathname !== canonicalPath) {
      if (requestedView && requestedView !== view) {
        clearSelections();
      }
      navigate(canonicalPath, { replace: true });
    }
  }, [
    clearSelections,
    featureFlagsLoaded,
    hasEnabledTabs,
    location.pathname,
    navigate,
    persistedState?.selectedApptId,
    persistedState?.selectedTaskId,
    persistedState?.taskFromReadinessId,
    requestedView,
    selectedAppt,
    selectedTask,
    taskFromReadiness,
    view,
  ]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadTaskNotifications();
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loadTaskNotifications]);

  useInterval(() => {
    if (isStreamSSEEnabled) return;
    if (document.visibilityState !== 'visible') return;

    loadTaskNotifications();
  }, 60 * 1000);

  useEffect(() => {
    if (document.visibilityState === 'visible' && isStreamSSEEnabled) {
      openTaskNotificationsStream();
    }

    return () => {
      closeTaskNotificationsStream();
    };
  }, [closeTaskNotificationsStream, isStreamSSEEnabled, openTaskNotificationsStream]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' || !isStreamSSEEnabled) {
        closeTaskNotificationsStream();
        return;
      }

      if (
        document.visibilityState === 'visible' &&
        isStreamSSEEnabled &&
        !taskNotificationsStreamRef.current
      ) {
        openTaskNotificationsStream();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [closeTaskNotificationsStream, isStreamSSEEnabled, openTaskNotificationsStream]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsTaskNotificationPreferencesLoading(true);

      try {
        const response = await getMyTaskNotificationPreferences();

        if (cancelled) return;

        setTaskNotificationPreferences(response || DEFAULT_NOTIFICATION_PREFERENCES);
      } catch (error) {
        if (cancelled) return;

        setTaskNotificationPreferences(DEFAULT_NOTIFICATION_PREFERENCES);
        showAlert(AlertType.Error, '', error?.message || 'Failed to load notification preferences');
      } finally {
        if (!cancelled) {
          setIsTaskNotificationPreferencesLoading(false);
        }
      }
    };

    const timeoutId = window.setTimeout(() => {
      run();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    getMyReadinessPinnedTeam()
      .then((response) => {
        if (cancelled) return;
        setReadinessPinnedTeam(response?.pinnedTeam ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setReadinessPinnedTeam(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    getMyPinnedDepartmentScope()
      .then((response) => {
        if (cancelled) return;
        setPinnedDepartmentScope(response?.pinnedScope ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setPinnedDepartmentScope(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (view !== TAB_KEYS.TASKS || (!requestedRoute.statusFilter && !requestedRoute.taskId)) return;

    const routeKey = `${location.key}:${location.pathname}`;
    if (appliedTaskRouteKeyRef.current === routeKey) return;

    if (ownerFilter !== 'all') {
      ownerFilterRef.current = 'all';
      setOwnerFilter('all');
      dispatch(saveCareOrchestrationState({ ownerFilter: 'all' }));
    }

    if (activeTeam !== 'all') {
      activeTeamRef.current = 'all';
      setActiveTeam('all');
      dispatch(saveCareOrchestrationState({ activeTeam: 'all' }));
    }

    const nextStatusFilter = requestedRoute.statusFilter;
    if (nextStatusFilter && statusFilter !== nextStatusFilter) {
      statusFilterRef.current = nextStatusFilter;
      setStatusFilter(nextStatusFilter);
      dispatch(saveCareOrchestrationState({ statusFilter: nextStatusFilter }));
    }

    if (requestedRoute.patientId) {
      expandPatientGroup(`patient-${requestedRoute.patientId}`);
    }

    if (!requestedRoute.taskId) {
      appliedTaskRouteKeyRef.current = routeKey;
      return;
    }

    if (isCareOrchestrationLoading) return;

    const requestedTask = allTasksById[requestedRoute.taskId] ?? tasksById[requestedRoute.taskId];
    const taskMatchesRoute =
      !!requestedTask &&
      (!requestedRoute.patientId ||
        `${getTaskPatientId(requestedTask) ?? ''}` === requestedRoute.patientId) &&
      (!requestedRoute.statusFilter ||
        matchesTaskStatusFilter(requestedTask, requestedRoute.statusFilter));

    if (taskMatchesRoute) {
      const requestedTaskPatient = {
        id: getTaskPatientId(requestedTask),
        firstName: requestedTask?.patient?.firstName || '',
        lastName: requestedTask?.patient?.lastName || '',
        preferredName: requestedTask?.patient?.preferredName || '',
        dob: requestedTask?.patient?.dob || null,
        mrn: requestedTask?.patient?.mrn || null,
        diabetesStatus: requestedTask?.patient?.diabetesStatus || null,
        cognitiveStatus: requestedTask?.patient?.cognitiveStatus || null,
      };

      if (`${patientSearch?.id ?? ''}` !== `${requestedTask.patientId}`) {
        handleSetPatientSearch(requestedTaskPatient);
      }

      expandPatientGroup(getTaskPatientGroupKey(requestedTask));

      if (selectedTask?.id !== requestedTask.id) {
        setSelectedTask(requestedTask);
        dispatch(saveCareOrchestrationState({ selectedTaskId: requestedTask.id }));
      }
      appliedTaskRouteKeyRef.current = routeKey;
      return;
    }

    if (!requestedTask) {
      if (hasMoreTasks && !isLoadingMoreTasks) {
        loadMoreTaskQueueData().catch(() => null);
      }
      return;
    }

    if (selectedTask || persistedState?.selectedTaskId) {
      setSelectedTask(null);
      dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
    }

    appliedTaskRouteKeyRef.current = routeKey;
  }, [
    activeTeam,
    dispatch,
    expandPatientGroup,
    isCareOrchestrationLoading,
    location.key,
    location.pathname,
    ownerFilter,
    patientSearch?.id,
    persistedState?.selectedTaskId,
    requestedRoute.patientId,
    requestedRoute.statusFilter,
    requestedRoute.taskId,
    selectedTask,
    statusFilter,
    allTasksById,
    tasksById,
    hasMoreTasks,
    isLoadingMoreTasks,
    loadMoreTaskQueueData,
    view,
  ]);

  useEffect(() => {
    if (!view) return;
    dispatch(saveCareOrchestrationView(view));
  }, [dispatch, view]);

  const handleChangeView = (nextView) => {
    if (!nextView || nextView === view) return;
    navigate(getCareOrchestrationPath(nextView));
  };

  const handleSelectedLeadTaskChange = useCallback(
    (leadTaskId) => {
      if (!leadTaskId || requestedRoute.leadTaskId === leadTaskId) return;
      navigate(getCareOrchestrationPath(TAB_KEYS.LEADS, { leadTaskId }), { replace: true });
    },
    [navigate, requestedRoute.leadTaskId],
  );

  const handleResetTaskRoute = () => {
    if (view !== TAB_KEYS.TASKS) return;

    const baseTasksPath = getCareOrchestrationPath(TAB_KEYS.TASKS);
    if (location.pathname === baseTasksPath) return;

    navigate(baseTasksPath, { replace: true });
  };

  const handleSetSelectedTask = (task) => {
    handleResetTaskRoute();
    setSelectedTask(task);
    dispatch(saveCareOrchestrationState({ selectedTaskId: task?.id || null }));
  };

  const handleSetOwnerFilter = (nextOwnerFilter) => {
    handleResetTaskRoute();
    ownerFilterRef.current = nextOwnerFilter;
    setOwnerFilter(nextOwnerFilter);
    dispatch(saveCareOrchestrationState({ ownerFilter: nextOwnerFilter }));
  };

  const handleSetActiveTeam = (nextActiveTeam) => {
    handleResetTaskRoute();
    activeTeamRef.current = nextActiveTeam;
    setActiveTeam(nextActiveTeam);
    dispatch(saveCareOrchestrationState({ activeTeam: nextActiveTeam }));
  };

  const handleSetStatusFilter = (nextStatusFilter) => {
    handleResetTaskRoute();
    statusFilterRef.current = nextStatusFilter;
    setStatusFilter(nextStatusFilter);
    dispatch(saveCareOrchestrationState({ statusFilter: nextStatusFilter }));
  };

  const handleChangeOrganizationScope = (nextScope) => {
    if (nextScope === organizationScopeRef.current) return;
    organizationScopeRef.current = nextScope;
    setOrganizationScope(nextScope);
  };

  const handleChangeWorkflowScope = (nextScope) => {
    if (nextScope === workflowScopeRef.current) return;
    workflowScopeRef.current = nextScope;
    setWorkflowScope(nextScope);

    if (nextScope === WORKFLOW_SCOPE.PRE_VISIT) {
      return;
    }

    if (!postVisitReadinessBodyRef.current) {
      setPostVisitDataReady(false);
      loadPostVisitReadinessAppointments();
    }
  };

  const handleSetDefaultOrganizationScope = (nextScope) => {
    const platform = nextScope === OrgScope.Mindspan ? Platform.Mindspan : Platform.Perry;
    setIsSettingDefaultOrgScope(true);
    dispatch(
      changeClientPlatformRequest(
        currentUserId,
        { platform },
        {
          successBlock: () => setIsSettingDefaultOrgScope(false),
          errorBlock: (err) => {
            showAlert(AlertType.Error, '', err || 'Failed to update default organization.');
            setIsSettingDefaultOrgScope(false);
          },
        },
      ),
    );
  };

  const handleSetTaskSortMode = (nextTaskSortMode) => {
    const normalizedTaskSortMode = Number(nextTaskSortMode);
    if (!Number.isInteger(normalizedTaskSortMode) || normalizedTaskSortMode === taskSortMode)
      return;

    handleResetTaskRoute();
    setTaskSortMode(normalizedTaskSortMode);
    setPatientSearch(null);
    setExpandedPatients({});
    dispatch(
      saveCareOrchestrationState({
        patientSearch: null,
        taskSortMode: normalizedTaskSortMode,
        expandedPatients: {},
      }),
    );
  };

  const handleSort = (nextKey) => {
    handleResetTaskRoute();
    if (nextKey === sortKey) {
      const nextSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(nextSortDirection);
      dispatch(saveCareOrchestrationState({ sortDirection: nextSortDirection }));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
    dispatch(
      saveCareOrchestrationState({
        sortKey: nextKey,
        sortDirection: 'asc',
      }),
    );
  };

  const handleSetSelectedAppt = (appointment) => {
    setSelectedAppt(appointment);
    dispatch(saveCareOrchestrationState({ selectedApptId: appointment?.id || null }));
  };

  const handleSetSelectedPostVisitAppt = (appointment) => {
    setSelectedPostVisitAppt(appointment);
    dispatch(saveCareOrchestrationState({ selectedPostVisitApptId: appointment?.id || null }));
  };

  const handleDismissPostVisitAppt = (apptId) => {
    const normalizedApptId = `${apptId}`;
    setDismissedPostVisitApptIds((prev) => new Set(prev).add(normalizedApptId));
    setSelectedPostVisitAppt((prev) => (`${prev?.id}` === normalizedApptId ? null : prev));
  };

  const handleRestoreAllPostVisitAppts = () => {
    setDismissedPostVisitApptIds(new Set());
  };

  const handleSetTaskFromReadiness = (task) => {
    setTaskFromReadiness(task);
    dispatch(saveCareOrchestrationState({ taskFromReadinessId: task?.id || null }));
  };

  const handleSetExpandedPatients = (nextExpandedPatients) => {
    handleResetTaskRoute();
    const resolvedExpandedPatients =
      typeof nextExpandedPatients === 'function'
        ? nextExpandedPatients(expandedPatients)
        : nextExpandedPatients;

    setExpandedPatients(resolvedExpandedPatients);
    dispatch(saveCareOrchestrationState({ expandedPatients: resolvedExpandedPatients }));
  };

  const scopedAppts = useMemo(() => {
    if (isAllScope) return sortedAppts;
    const scopedIds = new Set(activeScopeDepartmentAthenaIds);
    return sortedAppts.filter((appointment) =>
      scopedIds.has(`${appointment?.departmentAthenaId ?? ''}`.trim()),
    );
  }, [activeScopeDepartmentAthenaIds, isAllScope, sortedAppts]);

  const readinessStats = useMemo(
    () =>
      scopedAppts.reduce(
        (acc, appointment) => {
          const readinessStatus = getAppointmentReadinessStatus(
            appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
          );

          if (readinessStatus === 'green') acc.green += 1;
          if (readinessStatus === 'yellow') acc.yellow += 1;
          if (readinessStatus === 'red') acc.red += 1;

          return acc;
        },
        { red: 0, yellow: 0, green: 0 },
      ),
    [appointmentTasksById, scopedAppts],
  );

  const patientScopedTasks = useMemo(() => {
    if (!patientSearch) return availableTasks;

    if (patientSearch.kind === 'reason') {
      return availableTasks.filter(
        (task) => Number(task?.reasonId) === Number(patientSearch.reasonId),
      );
    }

    if (patientSearch.kind === 'priority') {
      return availableTasks.filter((task) => `${task?.priority || ''}` === patientSearch.priority);
    }

    if (patientSearch.id == null) return availableTasks;

    return availableTasks.filter(
      (task) => `${getTaskPatientId(task) ?? ''}` === `${patientSearch.id}`,
    );
  }, [availableTasks, patientSearch]);

  const taskQueueTotals = useMemo(() => {
    if (hasFullTaskQueueMeta(taskQueueMeta)) return taskQueueMeta;
    if (hasFullTaskQueueMeta(careOrchestrationMeta?.tasks)) return careOrchestrationMeta.tasks;
    return null;
  }, [careOrchestrationMeta?.tasks, taskQueueMeta]);

  const filtered = useMemo(() => {
    const passesNonTeamFilters = (task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      const isUnassigned = !ownerLabel || ownerLabel === 'unassigned';
      const isMyTask = !!currentUserNameLower && ownerLabel === currentUserNameLower;
      const isFollowingTask = isTaskFollowedByCurrentUser(task, currentUserId);
      const isSessionCompletedTask = !!sessionCompletedTaskIds[task.id];

      if (ownerFilter === 'my' && !isMyTask) return false;
      if (ownerFilter === 'following' && !isFollowingTask) return false;
      if (ownerFilter === 'unassigned' && !isUnassigned) return false;
      if (!isSessionCompletedTask && !matchesTaskStatusFilter(task, statusFilter)) return false;

      return true;
    };

    const nextFilteredTasks = patientScopedTasks.filter((task) => {
      if (!passesNonTeamFilters(task)) return false;
      if (activeTeam !== 'all' && task.team !== activeTeam) return false;
      return true;
    });

    const filteredIdSet = new Set(nextFilteredTasks.map((t) => t.id));

    patientScopedTasks.forEach((task) => {
      if (filteredIdSet.has(task.id)) return;
      const parentTaskId = `${task.parentTaskId || ''}`.trim();
      if (parentTaskId && filteredIdSet.has(parentTaskId)) {
        nextFilteredTasks.push(task);
        filteredIdSet.add(task.id);
      }
    });

    patientScopedTasks.forEach((task) => {
      if (filteredIdSet.has(task.id)) return;
      const parentTaskId = `${task.parentTaskId || ''}`.trim();
      if (!parentTaskId) {
        const hasMatchingSubtask = patientScopedTasks.some(
          (sub) => `${sub.parentTaskId || ''}`.trim() === task.id && filteredIdSet.has(sub.id),
        );
        if (hasMatchingSubtask) {
          nextFilteredTasks.push(task);
          filteredIdSet.add(task.id);
        }
      }
    });

    patientScopedTasks.forEach((task) => {
      if (filteredIdSet.has(task.id)) return;
      const parentTaskId = `${task.parentTaskId || ''}`.trim();
      if (parentTaskId && filteredIdSet.has(parentTaskId)) {
        nextFilteredTasks.push(task);
        filteredIdSet.add(task.id);
      }
    });

    if (taskSortMode === TASK_QUEUE_SORT_MODE.PatientPriority && !patientSearch) {
      return nextFilteredTasks;
    }

    return nextFilteredTasks.sort((a, b) => {
      const priority = {
        [TASK_PRIORITY.Critical]: 0,
        [TASK_PRIORITY.High]: 1,
        [TASK_PRIORITY.Medium]: 2,
        [TASK_PRIORITY.Low]: 3,
      };
      const statusOrder = {
        pending: 0,
        'in-progress': 1,
        [TASK_DISPLAY_STATUS.Done]: 2,
      };

      const dir = sortDirection === 'asc' ? 1 : -1;
      let primary = 0;

      if (sortKey === 'status') {
        primary =
          (statusOrder[getTaskDisplayStatus(a)] ?? 99) -
          (statusOrder[getTaskDisplayStatus(b)] ?? 99);
      } else if (sortKey === 'priority') {
        primary = (priority[a.priority] ?? 99) - (priority[b.priority] ?? 99);
      } else {
        const aDue = a.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
        const bDue = b.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
        primary = aDue - bDue;
      }

      if (primary !== 0) return dir * primary;

      const aDue = a.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
      const bDue = b.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
      return aDue - bDue || priority[a.priority] - priority[b.priority];
    });
  }, [
    activeTeam,
    currentUserId,
    currentUserNameLower,
    ownerFilter,
    patientSearch,
    patientScopedTasks,
    sessionCompletedTaskIds,
    sortDirection,
    sortKey,
    statusFilter,
    taskSortMode,
  ]);

  useEffect(() => {
    if (!selectedTask) return;
    if (selectedTask?.parentTaskId) return;

    const hasSelectedTask = filtered.some((task) => task.id === selectedTask.id);
    if (hasSelectedTask) return;

    setSelectedTask(null);
    dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
  }, [dispatch, filtered, selectedTask]);

  const ownerStats = useMemo(() => {
    if (!patientSearch && taskQueueTotals) {
      return {
        all: taskQueueTotals.totalAll ?? 0,
        my: taskQueueTotals.totalMyTasks ?? 0,
        following: taskQueueTotals.totalFollowing ?? 0,
        unassigned: taskQueueTotals.totalUnassigned ?? 0,
      };
    }

    if (!patientScopedTasks.length) return DEFAULT_OWNER_STATS;

    const baseTasks = patientScopedTasks.filter((task) => {
      if (activeTeam !== 'all' && task.team !== activeTeam) return false;
      return matchesTaskStatusFilter(task, statusFilter);
    });

    const my = baseTasks.filter((task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      return !!currentUserNameLower && ownerLabel === currentUserNameLower;
    }).length;

    const unassigned = baseTasks.filter((task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      return !ownerLabel || ownerLabel === 'unassigned';
    }).length;

    const following = baseTasks.filter((task) =>
      isTaskFollowedByCurrentUser(task, currentUserId),
    ).length;

    return {
      all: baseTasks.length,
      my,
      following,
      unassigned,
    };
  }, [
    activeTeam,
    currentUserId,
    currentUserNameLower,
    patientScopedTasks,
    patientSearch,
    statusFilter,
    taskQueueTotals,
  ]);

  const stats = useMemo(() => {
    if (!patientSearch && taskQueueTotals) {
      return {
        total: taskQueueTotals.totalUncompleted ?? taskQueueTotals.total ?? 0,
        member: taskQueueTotals.totalMember ?? 0,
        ops: taskQueueTotals.totalOps ?? 0,
        clinical: taskQueueTotals.totalClinical ?? 0,
        rcm: taskQueueTotals.totalRcm ?? 0,
        cognitive: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'cognitive' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        kisunla: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'kisunla' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-iv': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-iv' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-sc': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-sc' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        critical: taskQueueTotals.totalCritical ?? 0,
        blocked: taskQueueTotals.totalBlocked ?? 0,
        escalated: taskQueueTotals.totalEscalated ?? 0,
        messages: taskQueueTotals.totalMessages ?? 0,
        assessments: taskQueueTotals.totalAssessments ?? 0,
      };
    }

    if (patientScopedTasks.length) {
      return {
        total: patientScopedTasks.filter(
          (task) => getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        member: patientScopedTasks.filter(
          (task) =>
            task.team === 'member' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        ops: patientScopedTasks.filter(
          (task) => task.team === 'ops' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        clinical: patientScopedTasks.filter(
          (task) =>
            task.team === 'clinical' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        rcm: patientScopedTasks.filter(
          (task) => task.team === 'rcm' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        cognitive: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'cognitive' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        kisunla: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'kisunla' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-iv': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-iv' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-sc': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-sc' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        critical: patientScopedTasks.filter(
          (task) =>
            task.priority === TASK_PRIORITY.Critical &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        blocked: patientScopedTasks.filter((task) => isTaskBlockedStatus(task)).length,
        escalated: patientScopedTasks.filter((task) => isTaskEscalatedStatus(task)).length,
        messages: patientScopedTasks.filter(
          (task) => isMessageTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        assessments: patientScopedTasks.filter(
          (task) =>
            isAssessmentTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
      };
    }

    return DEFAULT_STATS;
  }, [patientScopedTasks, patientSearch, taskQueueTotals]);

  const totalTaskCount = useMemo(() => {
    if (!patientSearch && taskQueueTotals) {
      return taskQueueTotals.totalFiltered ?? filtered.length;
    }

    return filtered.length;
  }, [filtered.length, patientSearch, taskQueueTotals]);

  return (
    <div className={styles.root}>
      <CareOrchestrationHeader
        view={view}
        ownerFilter={ownerFilter}
        readinessStats={readinessStats}
        stats={stats}
        leadTaskCount={leadTaskCount}
        tabAvailability={{
          [TAB_KEYS.READINESS]: isReadinessEnabled,
          [TAB_KEYS.TASKS]: isTasksEnabled,
          [TAB_KEYS.LEADS]: isTasksEnabled,
          [TAB_KEYS.BULK_ASSIGN]: isTasksEnabled,
        }}
        onChangeView={(nextView) => {
          const isNextViewEnabled = availableTabs.some(
            (tab) => tab.key === nextView && tab.enabled,
          );
          if (!isNextViewEnabled) return;
          handleChangeView(nextView);
        }}
        onClearSelections={clearSelections}
        taskNotificationPreferences={taskNotificationPreferences}
        isTaskNotificationPreferencesLoading={isTaskNotificationPreferencesLoading}
        isTaskNotificationPreferencesSaving={isTaskNotificationPreferencesSaving}
        onPatchTaskNotificationPreferences={patchTaskNotificationPreferences}
        onSearchTaskNotificationSlackUsers={searchTaskNotificationSlackUsers}
        onSendTaskNotificationTestMessage={sendTaskNotificationTestMessage}
        taskNotifications={taskNotifications}
        isTaskNotificationsLoading={isTaskNotificationsLoading}
        onDismissTaskNotification={handleDismisstaskNotification}
        onOpenTaskNotification={handleOpenTaskNotification}
        onMarkAllTaskNotificationsRead={handleMarkAllTaskNotificationsRead}
        browserNotificationPermission={browserNotificationPermission}
        onRequestBrowserNotificationPermission={handleRequestBrowserNotificationPermission}
        organizationScope={organizationScope}
        defaultOrgScope={defaultOrgScope}
        onChangeOrganizationScope={handleChangeOrganizationScope}
        onSetDefaultOrganizationScope={handleSetDefaultOrganizationScope}
        isSettingDefaultOrganizationScope={isSettingDefaultOrgScope}
      />

      <div className={styles.content}>
        <div className={styles.contentInner}>
          {!hasEnabledTabs && (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateTitle}>Care Orchestration is coming soon</div>
              <div className={styles.emptyStateText}>
                Tasks and Readiness will appear here once those feature flags are enabled.
              </div>
            </div>
          )}

          {hasEnabledTabs && view === TAB_KEYS.READINESS && (
            <ReadinessView
              isLoading={isCareOrchestrationLoading || !readinessDataReady}
              workflowScope={workflowScope}
              onChangeWorkflowScope={handleChangeWorkflowScope}
              apptsByDate={apptsByDate}
              readinessStats={readinessStats}
              selectedAppt={selectedAppt}
              taskFromReadiness={taskFromReadiness}
              appointmentTasksById={appointmentTasksById}
              postVisitIsLoading={isCareOrchestrationLoading || !postVisitDataReady}
              postApptsByDate={postApptsByDate}
              postVisitAppointmentTasksById={postVisitAppointmentTasksById}
              pinnedTeam={readinessPinnedTeam}
              onTogglePinnedTeam={toggleReadinessPinnedTeam}
              departments={departmentDefinitions}
              departmentLabelById={departmentLabelById}
              scopeFilters={scopeFilters}
              onChangeScopeFilters={setScopeFilters}
              guideEnabled={guideEnabled}
              onToggleGuide={setGuideEnabled}
              isScopePaused={isScopePaused}
              onToggleScopePause={() => setIsScopePaused((prev) => !prev)}
              isPinnedToCurrentScope={isPinnedToCurrentScope}
              canResetToPinnedScope={canResetToPinnedScope}
              showScopeUtilities={showScopeUtilities}
              onToggleScopePin={toggleScopePin}
              onResetScopeToPinned={resetScopeToPinned}
              selectedPostVisitAppt={selectedPostVisitAppt}
              setSelectedPostVisitAppt={handleSetSelectedPostVisitAppt}
              dismissedPostVisitCount={dismissedPostVisitApptIds.size}
              onDismissPostVisitAppt={handleDismissPostVisitAppt}
              onRestoreAllPostVisitAppts={handleRestoreAllPostVisitAppts}
              getAppointmentTaskLookupKey={getAppointmentTaskLookupKey}
              setSelectedAppt={handleSetSelectedAppt}
              setTaskFromReadiness={handleSetTaskFromReadiness}
              onMarkComplete={handleMarkComplete}
              markingTaskId={markingTaskId}
              onCancelTask={handleCancelTask}
              cancelingTaskId={cancelingTaskId}
              onReassignTask={handleReassignTask}
              reassigningTaskId={reassigningTaskId}
              onFollowTask={handleFollowTask}
              followingTaskId={followingTaskId}
              onUnfollowTask={handleUnfollowTask}
              unfollowingTaskId={unfollowingTaskId}
              onAssignToMe={handleAssignToMe}
              assigningTaskId={assigningTaskIds}
              onUnassignTask={handleUnassignTask}
              unassigningTaskId={unassigningTaskId}
              onBlockTask={handleBlockTask}
              blockingTaskId={blockingTaskId}
              onUnblockTask={handleUnblockTask}
              unblockingTaskId={unblockingTaskId}
              onEscalateTask={handleEscalateTask}
              escalatingTaskId={escalatingTaskId}
              onDeescalateTask={handleDeescalateTask}
              deescalatingTaskId={deescalatingTaskId}
              onDelegateSubtasks={handleDelegateSubtasks}
              delegatingSubtasksTaskId={delegatingSubtasksTaskId}
              onCancelSubtask={handleCancelSubtask}
              cancelingSubtaskId={cancelingSubtaskId}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              cortexOrigin={cortexOrigin}
              onReplySent={refreshCareOrchestrationData}
              delegatedSubtasksByParentId={delegatedSubtasksByParentId}
            />
          )}

          {hasEnabledTabs && view === TAB_KEYS.BULK_ASSIGN && (
            <BulkAssignView
              isLoading={isCareOrchestrationLoading}
              departments={departmentDefinitions}
              scopeFilters={scopeFilters}
              onChangeScopeFilters={setScopeFilters}
              guideEnabled={guideEnabled}
              onToggleGuide={setGuideEnabled}
              isScopePaused={isScopePaused}
              onToggleScopePause={() => setIsScopePaused((prev) => !prev)}
              isPinnedToCurrentScope={isPinnedToCurrentScope}
              canResetToPinnedScope={canResetToPinnedScope}
              showScopeUtilities={showScopeUtilities}
              onToggleScopePin={toggleScopePin}
              onResetScopeToPinned={resetScopeToPinned}
              ownerFilter={ownerFilter}
              setOwnerFilter={handleSetOwnerFilter}
              ownerStats={ownerStats}
              activeTeam={activeTeam}
              setActiveTeam={handleSetActiveTeam}
              statusFilter={statusFilter}
              setStatusFilter={handleSetStatusFilter}
              patientSearch={patientSearch}
              setPatientSearch={handleSetPatientSearch}
              stats={stats}
              filtered={filtered}
              totalTaskCount={totalTaskCount}
              taskQueueTotals={taskQueueTotals}
              searchableTasks={availableTasks}
              taskSortMode={taskSortMode}
              setTaskSortMode={handleSetTaskSortMode}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              isExpandingTaskSearch={isExpandingTaskSearch}
              onExpandTaskSearchScope={expandTaskSearchScope}
              onSearchTaskPatients={handleSearchTaskPatients}
              onClearTaskSearch={handleClearTaskSearch}
              hasMoreTasks={!patientSearch && hasMoreTasks}
              isLoadingMoreTasks={isLoadingMoreTasks}
              onLoadMoreTasks={loadMoreTaskQueueData}
              expandedPatients={expandedPatients}
              setExpandedPatients={handleSetExpandedPatients}
              selectedTaskIds={selectedTaskIds}
              setSelectedTaskIds={setSelectedTaskIds}
              onBulkApply={handleBulkApply}
            />
          )}

          {hasEnabledTabs && view === TAB_KEYS.LEADS && (
            <LeadTasksView
              onCountChange={setLeadTaskCount}
              requestedTaskId={requestedRoute.leadTaskId}
              requestedLeadResourceId={requestedRoute.leadResourceId}
              requestedClaim={!!requestedRoute.claim}
              onSelectedTaskChange={handleSelectedLeadTaskChange}
            />
          )}

          {hasEnabledTabs && view === TAB_KEYS.TASKS && (
            <TasksView
              isLoading={isCareOrchestrationLoading}
              departments={departmentDefinitions}
              scopeFilters={scopeFilters}
              onChangeScopeFilters={setScopeFilters}
              guideEnabled={guideEnabled}
              onToggleGuide={setGuideEnabled}
              isScopePaused={isScopePaused}
              onToggleScopePause={() => setIsScopePaused((prev) => !prev)}
              isPinnedToCurrentScope={isPinnedToCurrentScope}
              canResetToPinnedScope={canResetToPinnedScope}
              showScopeUtilities={showScopeUtilities}
              onToggleScopePin={toggleScopePin}
              onResetScopeToPinned={resetScopeToPinned}
              ownerFilter={ownerFilter}
              setOwnerFilter={handleSetOwnerFilter}
              ownerStats={ownerStats}
              currentUserNameLower={currentUserNameLower}
              activeTeam={activeTeam}
              setActiveTeam={handleSetActiveTeam}
              statusFilter={statusFilter}
              setStatusFilter={handleSetStatusFilter}
              patientSearch={patientSearch}
              setPatientSearch={handleSetPatientSearch}
              stats={stats}
              filtered={filtered}
              totalTaskCount={totalTaskCount}
              taskQueueTotals={taskQueueTotals}
              searchableTasks={availableTasks}
              taskSortMode={taskSortMode}
              setTaskSortMode={handleSetTaskSortMode}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              isExpandingTaskSearch={isExpandingTaskSearch}
              onExpandTaskSearchScope={expandTaskSearchScope}
              onSearchTaskPatients={handleSearchTaskPatients}
              onClearTaskSearch={handleClearTaskSearch}
              hasMoreTasks={!patientSearch && hasMoreTasks}
              isLoadingMoreTasks={isLoadingMoreTasks}
              onLoadMoreTasks={loadMoreTaskQueueData}
              selectedTask={selectedTask}
              setSelectedTask={handleSetSelectedTask}
              expandedPatients={expandedPatients}
              setExpandedPatients={handleSetExpandedPatients}
              onAssignToMe={handleAssignToMe}
              assigningTaskId={assigningTaskIds}
              onUnassignTask={handleUnassignTask}
              unassigningTaskId={unassigningTaskId}
              onMarkComplete={handleMarkComplete}
              markingTaskId={markingTaskId}
              onCancelTask={handleCancelTask}
              cancelingTaskId={cancelingTaskId}
              onReassignTask={handleReassignTask}
              reassigningTaskId={reassigningTaskId}
              onFollowTask={handleFollowTask}
              followingTaskId={followingTaskId}
              onUnfollowTask={handleUnfollowTask}
              unfollowingTaskId={unfollowingTaskId}
              onBlockTask={handleBlockTask}
              blockingTaskId={blockingTaskId}
              onUnblockTask={handleUnblockTask}
              unblockingTaskId={unblockingTaskId}
              onEscalateTask={handleEscalateTask}
              escalatingTaskId={escalatingTaskId}
              onDeescalateTask={handleDeescalateTask}
              deescalatingTaskId={deescalatingTaskId}
              onDelegateSubtasks={handleDelegateSubtasks}
              delegatingSubtasksTaskId={delegatingSubtasksTaskId}
              onCancelSubtask={handleCancelSubtask}
              cancelingSubtaskId={cancelingSubtaskId}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              cortexOrigin={cortexOrigin}
              onReplySent={refreshCareOrchestrationData}
              delegatedSubtasksByParentId={delegatedSubtasksByParentId}
            />
          )}
        </div>
      </div>
      <TaskNotificationToasts
        toasts={taskNotificationToasts}
        onClose={handleCloseTaskNotificationToast}
        onOpen={handleOpenTaskNotificationToast}
      />
      {bulkAssignToast && (
        <BulkAssignToast
          message={bulkAssignToast.message}
          action={bulkAssignToast.action}
          onClose={() => setBulkAssignToast(null)}
        />
      )}
    </div>
  );
}
